import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  PaperAirplaneIcon,
  FunnelIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  CalendarDaysIcon,
  AcademicCapIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { universities, universityPrograms } from '../data/mockData';
import { 
  useGetStudentRequestStatusesQuery,
  useGetStudentApplicationsQuery,
  useGetStudentApplicationQuery,
  useAddStudentApplicationMutation,
  useUpdateStudentApplicationMutation,
  useDeleteStudentApplicationMutation,
  useAddStatusChangeMutation,
  useGetUniversityRecommendationsQuery,
  useSendUniversityOffersMutation,
  useGetUniversityOffersQuery,
  useGetApplicationStatusHistoryQuery,
  useAcceptUniversityOfferMutation,
  useCheckStudentByPhoneQuery,
  useGetApplicationSourcesQuery,
  useGetEducationLevelsQuery,
  useGetActiveCountriesQuery,
  useGetConsultantsQuery,
  useGetActiveUniversitiesQuery
} from '../store/api';
import { Audio } from 'react-loader-spinner';
import { usePermission } from '../hooks/useAuth';

export default function StudentRequests() {
  const navigate = useNavigate();
  
  // Permission hooks - strictly permission-based
  const canAddStudentRequest = usePermission('student_request.add');
  const canEditStudentRequest = usePermission('student_request.edit');
  const canDeleteStudentRequest = usePermission('student_request.delete');
  const canViewStudentRequest = usePermission('student_request.view'); // Already being used in route protection
  const canAcceptStudentRequest = usePermission('student_request.accept');
  const canChangeStudentRequestStatus = usePermission('student_request.status_change');
  const canManageUniversityRequests = usePermission('student_request.university_requests');
  
  // API hooks
  const { data: statusesResponse, error: statusError, isLoading: statusesLoading } = useGetStudentRequestStatusesQuery();
  const { data: applicationsResponse, error: applicationsError, isLoading: applicationsLoading, refetch: refetchApplications } = useGetStudentApplicationsQuery();
  

  const { data: sourcesResponse, isLoading: sourcesLoading } = useGetApplicationSourcesQuery();
  const { data: educationLevelsResponse, isLoading: educationLoading } = useGetEducationLevelsQuery();
  const { data: countriesResponse, isLoading: countriesLoading } = useGetActiveCountriesQuery();
  const { data: consultantsResponse, isLoading: consultantsLoading } = useGetConsultantsQuery();
  const { data: universitiesResponse, isLoading: universitiesLoading } = useGetActiveUniversitiesQuery();
  
  // Mutations
  const [addApplication] = useAddStudentApplicationMutation();
  const [updateApplication] = useUpdateStudentApplicationMutation();
  const [deleteApplication] = useDeleteStudentApplicationMutation();
  const [addStatusChange] = useAddStatusChangeMutation();
  const [sendUniversityOffers] = useSendUniversityOffersMutation();
  const [acceptUniversityOffer] = useAcceptUniversityOfferMutation();
  
  // Phone lookup state
  const [phoneQuery, setPhoneQuery] = useState('');
  const [phoneTimeout, setPhoneTimeout] = useState(null);
  const [existingStudent, setExistingStudent] = useState(null);
  const [fieldsDisabled, setFieldsDisabled] = useState(false);
  
  // Dropdown states
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  
  // Data states
  const [statuses, setStatuses] = useState([]);
  const [students, setStudents] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [sourcesOptions, setSourcesOptions] = useState([]);
  const [educationLevels, setEducationLevels] = useState([]);
  const [countries, setCountries] = useState([]);
  const [universities, setUniversities] = useState([]);
  
  // University recommendations state
  const [recommendationParams, setRecommendationParams] = useState(null);
  
  // Skip phone query if empty
  const { data: phoneCheckResult } = useCheckStudentByPhoneQuery(phoneQuery, {
    skip: !phoneQuery || phoneQuery.length < 9
  });

  // University recommendations query
  const { data: recommendationsResponse, isLoading: recommendationsLoading } = useGetUniversityRecommendationsQuery(recommendationParams, {
    skip: !recommendationParams
  });
  
  // Handle API responses with useEffect
  useEffect(() => {
    if (!statusesLoading && statusesResponse) {
      if (Array.isArray(statusesResponse)) {
        setStatuses(statusesResponse);
      } else if (statusesResponse.data && Array.isArray(statusesResponse.data)) {
        setStatuses(statusesResponse.data);
      } else if (statusesResponse.results && Array.isArray(statusesResponse.results)) {
        setStatuses(statusesResponse.results);
      }
    }
  }, [statusesResponse, statusesLoading]);

  useEffect(() => {
    if (!applicationsLoading && applicationsResponse) {
      let studentsData = [];
      
      // Check for nested Laravel pagination format: { status: true, data: { data: [...] } }
      if (applicationsResponse.status && applicationsResponse.data && applicationsResponse.data.data && Array.isArray(applicationsResponse.data.data)) {
        // Transform student applications to student format
        studentsData = applicationsResponse.data.data.map(application => ({
          id: application.student.id, // For backwards compatibility, this remains as student ID
          studentId: application.student.id, // Explicit student ID
          firstName: application.student.first_name || '',
          lastName: application.student.last_name || '',
          phone: application.student.phone || '',
          email: application.student.email || '',
          birthDate: application.student.birth_date || '',
          address: application.student.address || '',
          parentPhone: application.student.parent_phone || '',
          parentName: application.student.parent_name || '',
          source: application.student.source || '',
          statusId: application.status_id,
          status: application.status,
          consultantId: application.student.consultant_id,
          consultant: application.student.consultant,
          educationLevel: application.student.education_level || '',
          interestedEducationLevel: application.student.interested_education_level_id || '',
          specialization: application.student.specialization || '',
          currentYear: application.student.current_year || '',
          currentUniversity: application.student.current_university || '',
          interestedCountries: application.student.interested_countries || '',
          financialForecast: application.student.financial_forecast || '',
          tuitionFeeRange: application.student.tuition_fee_range || '',
          languageExam: application.student.language_exam || '',
          languageExamDate: application.student.language_exam_date || '',
          otherExams: application.student.other_exams || '',
          gpa: application.student.gpa || '',
          generalNotes: application.student.general_notes || '',
          followUpDone: application.student.follow_up_done || false,
          lastFollowUpDate: application.student.last_follow_up_date || '',
          firstContactDate: application.student.first_contact_date || application.application_date || '',
          appliedUniversities: application.student.applied_universities || '',
          // Keep application-specific data
          applicationId: application.id,
          universityId: application.university_id,
          programId: application.program_id,
          applicationDate: application.application_date,
          deadline: application.deadline,
          requiredDocuments: application.required_documents,
          applicationNotes: application.notes,
          university: application.university,
          program: application.program
        }));
        
        setStudents(studentsData);
      }
      // Laravel pagination format: { data: [...] }
      else if (applicationsResponse.data && Array.isArray(applicationsResponse.data)) {
        setStudents(applicationsResponse.data);
      }
      // Direct array format
      else if (Array.isArray(applicationsResponse)) {
        setStudents(applicationsResponse);
      }
      // Results format
      else if (applicationsResponse.results && Array.isArray(applicationsResponse.results)) {
        setStudents(applicationsResponse.results);
      } 
      else {
        setStudents([]);
      }
    }
  }, [applicationsResponse, applicationsLoading]);

  useEffect(() => {
    if (!consultantsLoading && consultantsResponse) {
      let consultantsData = [];
      
      if (Array.isArray(consultantsResponse)) {
        consultantsData = consultantsResponse;
      } else if (consultantsResponse.data && Array.isArray(consultantsResponse.data)) {
        consultantsData = consultantsResponse.data;
      } else if (consultantsResponse.results && Array.isArray(consultantsResponse.results)) {
        consultantsData = consultantsResponse.results;
      }

      // Transform consultants to add name property from admin data
      const transformedConsultants = consultantsData.map(consultant => ({
        ...consultant,
        name: consultant.admin ? `${consultant.admin.first_name} ${consultant.admin.last_name}` : 'İsimsiz',
        isActive: consultant.is_active && consultant.admin?.is_active
      }));

      setConsultants(transformedConsultants);
    }
  }, [consultantsResponse, consultantsLoading]);

  useEffect(() => {
    if (!sourcesLoading && sourcesResponse) {
      if (Array.isArray(sourcesResponse)) {
        setSourcesOptions(sourcesResponse);
      } else if (sourcesResponse.data && Array.isArray(sourcesResponse.data)) {
        setSourcesOptions(sourcesResponse.data);
      } else if (sourcesResponse.results && Array.isArray(sourcesResponse.results)) {
        setSourcesOptions(sourcesResponse.results);
      }
    }
  }, [sourcesResponse, sourcesLoading]);

  useEffect(() => {
    if (!educationLoading && educationLevelsResponse) {
      if (Array.isArray(educationLevelsResponse)) {
        setEducationLevels(educationLevelsResponse);
      } else if (educationLevelsResponse.data && Array.isArray(educationLevelsResponse.data)) {
        setEducationLevels(educationLevelsResponse.data);
      } else if (educationLevelsResponse.results && Array.isArray(educationLevelsResponse.results)) {
        setEducationLevels(educationLevelsResponse.results);
      }
    }
  }, [educationLevelsResponse, educationLoading]);

  useEffect(() => {
    if (!countriesLoading && countriesResponse) {
      let countriesData = [];
      
      if (Array.isArray(countriesResponse)) {
        countriesData = countriesResponse;
      } else if (countriesResponse.data && Array.isArray(countriesResponse.data)) {
        countriesData = countriesResponse.data;
      } else if (countriesResponse.results && Array.isArray(countriesResponse.results)) {
        countriesData = countriesResponse.results;
      }

      // Transform countries to add isActive property from is_active
      const transformedCountries = countriesData.map(country => ({
        ...country,
        isActive: country.is_active === 1 || country.is_active === true
      }));

      setCountries(transformedCountries);
    }
  }, [countriesResponse, countriesLoading]);

  useEffect(() => {
    if (!universitiesLoading && universitiesResponse) {
      let universitiesData = [];
      
      if (Array.isArray(universitiesResponse)) {
        universitiesData = universitiesResponse;
      } else if (universitiesResponse.data && Array.isArray(universitiesResponse.data)) {
        universitiesData = universitiesResponse.data;
      } else if (universitiesResponse.results && Array.isArray(universitiesResponse.results)) {
        universitiesData = universitiesResponse.results;
      }

      setUniversities(universitiesData);
    }
  }, [universitiesResponse, universitiesLoading]);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [consultantFilter, setConsultantFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [educationFilter, setEducationFilter] = useState('');


  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isUniversityModalOpen, setIsUniversityModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isAcceptanceModalOpen, setIsAcceptanceModalOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [viewingStudent, setViewingStudent] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [selectedUniversities, setSelectedUniversities] = useState([]);
  const [selectedRecommendations, setSelectedRecommendations] = useState([]);
  const [acceptanceData, setAcceptanceData] = useState({
    universityId: '',
    universityName: '',
    programId: '',
    programName: '',
    acceptanceDate: new Date().toISOString().split('T')[0],
    startDate: '',
    tuitionFee: '',
    scholarshipAmount: '',
    notes: '',
    documentsRequired: ''
  });
  const [statusChangeData, setStatusChangeData] = useState({
    toStatusId: '',
    reason: '',
    notes: ''
  });
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    birthDate: '',
    address: '',
    parentPhone: '',
    parentName: '',
    source: '',
    firstContactDate: '',
    statusId: 1,
    consultantId: '',
    educationLevel: '',
    interestedEducationLevel: '',
    specialization: '',
    currentYear: '',
    currentUniversity: '',
    universityId: '',
    interestedCountries: '',
    financialForecast: '',
    tuitionFeeRange: '',
    languageExam: '',
    languageExamDate: '',
    otherExams: '',
    gpa: '',
    generalNotes: '',
    appliedUniversities: '',
    requiredDocuments: '',
    followUpDone: false,
    lastFollowUpDate: ''
  });

  // University offers query (for viewing sent offers)
  const { data: universityOffersResponse, isLoading: offersLoading } = useGetUniversityOffersQuery(viewingStudent?.applicationId, {
    skip: !viewingStudent?.applicationId
  });

  // Status history query
  const { data: statusHistoryResponse, isLoading: statusHistoryLoading } = useGetApplicationStatusHistoryQuery(viewingStudent?.applicationId, {
    skip: !viewingStudent?.applicationId
  });

  // University offers query for selected student (for recommendations modal)
  const { data: selectedStudentOffersResponse, isLoading: selectedStudentOffersLoading, refetch: refetchSelectedStudentOffers } = useGetUniversityOffersQuery(selectedStudent?.applicationId, {
    skip: !selectedStudent?.applicationId || !isUniversityModalOpen
  });

  // University offers query for acceptance modal
  const { data: acceptanceOffersResponse, isLoading: acceptanceOffersLoading } = useGetUniversityOffersQuery(selectedStudent?.applicationId, {
    skip: !selectedStudent?.applicationId || !isAcceptanceModalOpen
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdownOpen && !event.target.closest('.country-dropdown')) {
        setCountryDropdownOpen(false);
        setCountrySearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [countryDropdownOpen]);

  // Phone lookup effect
  useEffect(() => {
    if (phoneCheckResult) {
      if (phoneCheckResult.status && phoneCheckResult.data) {
        // Helper function to extract date from datetime string
        const extractDateFromDateTime = (dateTimeString) => {
          if (!dateTimeString) return '';
          // If it's already in YYYY-MM-DD format, return as is
          if (dateTimeString.match(/^\d{4}-\d{2}-\d{2}$/)) {
            return dateTimeString;
          }
          // If it's in datetime format like 2025-07-01T00:00:00.000000Z, extract date part
          if (dateTimeString.includes('T')) {
            return dateTimeString.split('T')[0];
          }
          return dateTimeString;
        };

        // Student exists, transform API data to frontend format
        const student = {
          id: phoneCheckResult.data.id,
          firstName: phoneCheckResult.data.first_name || '',
          lastName: phoneCheckResult.data.last_name || '',
          phone: phoneCheckResult.data.phone || '',
          email: phoneCheckResult.data.email || '',
          birthDate: phoneCheckResult.data.birth_date || '',
          address: phoneCheckResult.data.address || '',
          parentPhone: phoneCheckResult.data.parent_phone || '',
          parentName: phoneCheckResult.data.parent_name || '',
          source: phoneCheckResult.data.source || '',
          firstContactDate: extractDateFromDateTime(phoneCheckResult.data.first_contact_date || ''),
          statusId: phoneCheckResult.data.status_id || 1,
          followUpDone: phoneCheckResult.data.follow_up_done || false,
          lastFollowUpDate: extractDateFromDateTime(phoneCheckResult.data.last_follow_up_date || ''),
          consultantId: phoneCheckResult.data.consultant_id || '',
          educationLevel: phoneCheckResult.data.education_level || '',
          specialization: phoneCheckResult.data.specialization || '',
          currentYear: phoneCheckResult.data.current_year || '',
          currentUniversity: phoneCheckResult.data.current_university || '',
          interestedCountries: phoneCheckResult.data.interested_countries || '',
          financialForecast: phoneCheckResult.data.financial_forecast || '',
          tuitionFeeRange: phoneCheckResult.data.tuition_fee_range || '',
          languageExam: phoneCheckResult.data.language_exam || '',
          languageExamDate: extractDateFromDateTime(phoneCheckResult.data.language_exam_date || ''),
          otherExams: phoneCheckResult.data.other_exams || '',
          gpa: phoneCheckResult.data.gpa || '',
          generalNotes: phoneCheckResult.data.general_notes || '',
          appliedUniversities: phoneCheckResult.data.applied_universities || '',
          requiredDocuments: phoneCheckResult.data.required_documents || '',
          status: phoneCheckResult.data.status,
          consultant: phoneCheckResult.data.consultant
        };

        setExistingStudent(student);
        // Handle interested countries properly - if it's already comma-separated IDs, use as is
        let interestedCountryIds = '';
        if (student.interestedCountries && typeof student.interestedCountries === 'string') {
          const trimmed = student.interestedCountries.trim();
          // Check if it's already comma-separated IDs (numeric)
          if (trimmed.match(/^[\d,\s]+$/)) {
            // It's already IDs, clean up spaces
            interestedCountryIds = trimmed.split(',').map(id => id.trim()).filter(id => id).join(',');
          } else {
            // It's country names, convert to IDs
            const countryNames = trimmed.split(',').map(name => name.trim());
            const countryIdsList = [];
            countryNames.forEach(name => {
              const country = countries.find(c => c.name.toLowerCase() === name.toLowerCase());
              if (country) {
                countryIdsList.push(country.id.toString());
              }
            });
            interestedCountryIds = countryIdsList.join(',');
          }
        }

        setFormData(prev => ({
          ...prev,
          firstName: student.firstName,
          lastName: student.lastName,
          email: student.email,
          phone: student.phone ? formatPhoneNumber(student.phone) : '+994',
          birthDate: student.birthDate,
          address: student.address,
          parentPhone: student.parentPhone,
          parentName: student.parentName,
          source: student.source,
          firstContactDate: student.firstContactDate,
          statusId: student.statusId,
          consultantId: student.consultantId.toString(),
          educationLevel: student.educationLevel || '',
          interestedEducationLevel: student.interestedEducationLevel?.toString() || '',
          specialization: student.specialization || '',
          currentYear: student.currentYear,
          currentUniversity: student.currentUniversity,
          universityId: phoneCheckResult.data.university_id?.toString() || '',
          interestedCountries: interestedCountryIds,
          financialForecast: student.financialForecast,
          tuitionFeeRange: student.tuitionFeeRange,
          languageExam: student.languageExam,
          languageExamDate: student.languageExamDate,
          otherExams: student.otherExams,
          gpa: student.gpa,
          generalNotes: student.generalNotes,
          appliedUniversities: student.appliedUniversities,
          requiredDocuments: student.requiredDocuments,
          followUpDone: student.followUpDone,
          lastFollowUpDate: student.lastFollowUpDate
        }));
        setFieldsDisabled(true);
      } else {
        // Student doesn't exist, enable fields for new student
        setExistingStudent(null);
        setFieldsDisabled(false);
      }
    }
  }, [phoneCheckResult, phoneQuery]);

  // Phone formatting functions
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, '');
    
    // If starts with 994, format as +994 XX XXX XX XX
    if (phoneNumber.startsWith('994')) {
      const formatted = phoneNumber.replace(/^994(\d{0,2})(\d{0,3})(\d{0,2})(\d{0,2}).*/, (match, p1, p2, p3, p4) => {
        let result = '+994';
        if (p1) result += ` ${p1}`;
        if (p2) result += ` ${p2}`;
        if (p3) result += ` ${p3}`;
        if (p4) result += ` ${p4}`;
        return result;
      });
      return formatted;
    }
    
    // If doesn't start with 994, add +994 prefix and format
    if (phoneNumber.length > 0) {
      const withPrefix = '994' + phoneNumber;
      return formatPhoneNumber(withPrefix);
    }
    
    return '+994';
  };

  const getUnmaskedPhone = (maskedPhone) => {
    // Remove all non-digits to get clean phone number
    return maskedPhone.replace(/\D/g, '');
  };

  // Handle phone input with debounce and masking
  const handlePhoneChange = (phone) => {
    // Format the phone number with mask
    const formattedPhone = formatPhoneNumber(phone);
    setFormData(prev => ({ ...prev, phone: formattedPhone }));
    
    // Clear previous timeout
    if (phoneTimeout) {
      clearTimeout(phoneTimeout);
    }

    // Get unmasked phone for API lookup
    const unmaskedPhone = getUnmaskedPhone(phone);

    // Reset fields if phone is empty
    if (!unmaskedPhone || unmaskedPhone.length < 4) {
      setPhoneQuery('');
      setExistingStudent(null);
      setFieldsDisabled(false);
      return;
    }

    // Set new timeout for phone lookup (use unmasked phone)
    const timeout = setTimeout(() => {
      if (unmaskedPhone.length >= 9) {
        setPhoneQuery(unmaskedPhone);
      }
    }, 500);
    
    setPhoneTimeout(timeout);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (phoneTimeout) {
        clearTimeout(phoneTimeout);
      }
    };
  }, [phoneTimeout]);



  // Filter students based on search and filters
  const filteredStudents = students.filter(student => {
    const matchesSearch = 
      student.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.phone.includes(searchTerm) ||
      (student.email && student.email.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = !statusFilter || String(student.statusId ?? '') === statusFilter;
    const matchesConsultant = !consultantFilter || String(student.consultantId ?? '') === consultantFilter;
    const matchesSource = !sourceFilter || 
      (student.source?.name || student.source) === sourceFilter ||
      student.source === sourceFilter;
    const matchesEducation = !educationFilter || 
      (student.educationLevel?.name || student.educationLevel) === educationFilter ||
      student.educationLevel === educationFilter;

    
    return matchesSearch && matchesStatus && matchesConsultant && matchesSource && matchesEducation;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedStudents = filteredStudents.slice(startIndex, startIndex + itemsPerPage);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, consultantFilter, sourceFilter, educationFilter]);

  // Statistics calculations
  const totalApplications = students.length;
  const acceptedApplications = students.filter(s => s.statusId === 6).length;
  const pendingApplications = students.filter(s => [1, 2, 3, 4, 5].includes(s.statusId)).length;
  const newApplicationsLast7Days = students.filter(s => {
    const contactDate = new Date(s.firstContactDate);
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return contactDate > sevenDaysAgo;
  }).length;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleAdd = () => {
    setEditingStudent(null);
    setExistingStudent(null);
    setFieldsDisabled(false);
    setPhoneQuery('');
    setCountryDropdownOpen(false);
    setFormData({
      firstName: '',
      lastName: '',
      phone: '+994',
      email: '',
      birthDate: '',
      address: '',
      parentPhone: '',
      parentName: '',
      source: '',
      firstContactDate: new Date().toISOString().split('T')[0],
      statusId: 1,
      consultantId: '',
      educationLevel: '',
      interestedEducationLevel: '',
      specialization: '',
      currentYear: '',
      currentUniversity: '',
      universityId: '',
      interestedCountries: '',
      financialForecast: '',
      tuitionFeeRange: '',
      languageExam: '',
      languageExamDate: '',
      otherExams: '',
      gpa: '',
      generalNotes: '',
      appliedUniversities: '',
      requiredDocuments: '',
      followUpDone: false,
      lastFollowUpDate: ''
    });
    setIsModalOpen(true);
  };

  const handleEdit = (student) => {
    
    setEditingStudent(student);
    setExistingStudent(student);
    setFieldsDisabled(false); // Allow editing all fields when editing existing application
    setPhoneQuery('');
    setCountryDropdownOpen(false);
    
    // Helper function to extract date from datetime string
    const extractDateFromDateTime = (dateTimeString) => {
      if (!dateTimeString) return '';
      // If it's already in YYYY-MM-DD format, return as is
      if (dateTimeString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dateTimeString;
      }
      // If it's in datetime format like 2025-07-01T00:00:00.000000Z, extract date part
      if (dateTimeString.includes('T')) {
        return dateTimeString.split('T')[0];
      }
      return dateTimeString;
    };
    
    const interestedEducationLevelValue = student.interestedEducationLevel?.toString() || '';
    
    setFormData({
      firstName: student.firstName || '',
      lastName: student.lastName || '',
      phone: student.phone ? formatPhoneNumber(student.phone) : '+994',
      email: student.email || '',
      birthDate: student.birthDate || '',
      address: student.address || '',
      parentPhone: student.parentPhone || '',
      parentName: student.parentName || '',
      source: student.source || '',
      firstContactDate: extractDateFromDateTime(student.firstContactDate || student.applicationDate || ''),
      statusId: student.statusId || 1,
      consultantId: student.consultantId?.toString() || '',
      educationLevel: student.educationLevel || '',
      interestedEducationLevel: interestedEducationLevelValue,
      specialization: student.specialization || '',
      currentYear: student.currentYear || '',
      currentUniversity: student.currentUniversity || '',
      universityId: student.universityId?.toString() || '',
      interestedCountries: (() => {
        // Handle interested countries - if it's already comma-separated IDs, use as is
        if (student.interestedCountries && typeof student.interestedCountries === 'string') {
          // Check if it's already comma-separated IDs (numeric)
          const trimmed = student.interestedCountries.trim();
          if (trimmed.match(/^[\d,\s]+$/)) {
            // It's already IDs, clean up spaces
            return trimmed.split(',').map(id => id.trim()).filter(id => id).join(',');
          } else {
            // It's country names, convert to IDs
            const countryNames = trimmed.split(',').map(c => c.trim());
            const countryIds = [];
            countryNames.forEach(name => {
              const country = countries.find(c => c.name.toLowerCase() === name.toLowerCase());
              if (country) {
                countryIds.push(country.id.toString());
              }
            });
            return countryIds.join(',');
          }
        } else if (Array.isArray(student.interestedCountries)) {
          return student.interestedCountries.join(',');
        }
        return '';
      })(),
      financialForecast: student.financialForecast || '',
      tuitionFeeRange: student.tuitionFeeRange || '',
      languageExam: student.languageExam || '',
      languageExamDate: extractDateFromDateTime(student.languageExamDate || ''),
      otherExams: student.otherExams || '',
      gpa: student.gpa || '',
      generalNotes: student.generalNotes || '',
      appliedUniversities: student.appliedUniversities || '',
      requiredDocuments: student.requiredDocuments || '',
      followUpDone: student.followUpDone || false,
      lastFollowUpDate: extractDateFromDateTime(student.lastFollowUpDate || '')
    });
    setIsModalOpen(true);
  };

  const handleView = (student) => {
    setViewingStudent(student);
    setActiveTab('details');
    setSelectedRecommendations([]);
    setIsViewModalOpen(true);
  };

  const handleDelete = async (applicationId, studentName) => {
    if (window.confirm(`"${studentName}" müraciətini silmək istədiyinizə əminsiniz?`)) {
      try {
        await deleteApplication(applicationId).unwrap();
        toast.success('Müraciət uğurla silindi!');
        refetchApplications();
      } catch (error) {
        console.error('Error deleting application:', error);
        
        // Enhanced error handling for backend validation errors
        if (error?.data && typeof error.data === 'object') {
          // Check if error response has the format: { status: false, errors: { field: ["message"] } }
          if (error.data.status === false && error.data.errors && typeof error.data.errors === 'object') {
            // Display each field error as separate toast
            Object.keys(error.data.errors).forEach(field => {
              const fieldErrors = error.data.errors[field];
              if (Array.isArray(fieldErrors)) {
                fieldErrors.forEach(errorMessage => {
                  // Translate field names to Azerbaijani for better UX
                  const fieldTranslations = {
                    'application_id': 'Müraciət',
                    'student_id': 'Tələbə',
                    'id': 'ID',
                    'general': 'Ümumi'
                  };
                  
                  const fieldName = fieldTranslations[field] || field;
                  toast.error(`${fieldName}: ${errorMessage}`);
                });
              } else if (typeof fieldErrors === 'string') {
                const fieldTranslations = {
                  'application_id': 'Müraciət',
                  'student_id': 'Tələbə',
                  'id': 'ID',
                  'general': 'Ümumi'
                };
                
                const fieldName = fieldTranslations[field] || field;
                toast.error(`${fieldName}: ${fieldErrors}`);
              }
            });
          }
          // Check if error response has a general message
          else if (error.data.message) {
            toast.error('Silmə zamanı xəta: ' + error.data.message);
          }
          // Fallback for other error formats
          else {
            toast.error('Silmə zamanı xəta baş verdi: ' + (error.message || 'Naməlum xəta'));
          }
        } else {
          // Fallback for non-object errors
          toast.error('Silmə zamanı xəta baş verdi: ' + (error.message || 'Naməlum xəta'));
        }
      }
    }
  };

  const handleSendUniversityRecommendations = (student) => {
    // Prevent sending recommendations to accepted applications
    if (student.statusId === 6) {
      toast.warning('Qəbul edilmiş müraciətlərə yeni təklif göndərilə bilməz!');
      return;
    }
    
    setSelectedStudent(student);
    setSelectedUniversities([]);
    
    // Find interested education level ID from the name or ID
    const interestedEducationLevelId = educationLevels.find(level => {
      const levelId = level?.id || level;
      const levelName = level?.name || level;
      return levelId?.toString() === student.interestedEducationLevel?.toString() || 
             levelName === student.interestedEducationLevel;
    })?.id || student.interestedEducationLevel;
    
    // Prepare parameters for university recommendations API
    const params = {
      interested_education_level_id: interestedEducationLevelId,
      specialization: student.specialization,
      interested_countries: student.interestedCountries,
      tuition_fee_range: student.tuitionFeeRange,
      financial_forecast: student.financialForecast
    };
    
    setRecommendationParams(params);
    setIsUniversityModalOpen(true);
  };

  const handleChangeStatus = (student) => {
    setSelectedStudent(student);
    setStatusChangeData({
      toStatusId: student.statusId?.toString() || '',
      reason: '',
      notes: ''
    });
    setIsStatusModalOpen(true);
  };

  const handleAcceptOffer = (student) => {
    // Prevent accepting offers for already accepted applications
    if (student.statusId === 6) {
      toast.warning('Qəbul edilmiş müraciətlərə yeni təklif qəbul edilə bilməz!');
      return;
    }
    
    setSelectedStudent(student);
    setAcceptanceData({
      offerId: '',
      universityId: '',
      universityName: '',
      programId: '',
      programName: '',
      acceptanceDate: new Date().toISOString().split('T')[0],
      startDate: '',
      tuitionFee: '',
      scholarshipAmount: '',
      notes: '',
      documentsRequired: ''
    });
    setIsAcceptanceModalOpen(true);
  };

  const handleStatusSubmit = async (e) => {
    e.preventDefault();
    try {
      const newStatusId = parseInt(statusChangeData.toStatusId);
      const newStatus = statuses.find(s => s.id === newStatusId);
      
      // Prepare data for status change tracking table
      const statusChangePayload = {
        student_id: selectedStudent.studentId || selectedStudent.id,
        application_id: selectedStudent.applicationId,
        from_status_id: selectedStudent.statusId,
        to_status_id: newStatusId,
        changed_by: 'İstifadəçi', // This should be the logged-in user
        changed_by_id: 1, // This should be the logged-in user ID
        change_date: new Date().toISOString().split('T')[0],
        change_time: new Date().toLocaleTimeString('en-GB', { hour12: false }),
        reason: statusChangeData.reason || null,
        notes: statusChangeData.notes || null
      };
      
      // Call API to create status change record
      await addStatusChange(statusChangePayload).unwrap();
      
      // Show success message
      toast.success(`Status "${selectedStudent.status?.name || 'Naməlum'}" -dan "${newStatus?.name || 'Naməlum'}" -a dəyişdirildi!`);
      
      // Refresh applications list
      refetchApplications();
      
      // Close modal and reset state
      setIsStatusModalOpen(false);
      setSelectedStudent(null);
      setStatusChangeData({
        toStatusId: '',
        reason: '',
        notes: ''
      });
    } catch (error) {
      console.error('Error updating status:', error);
      
      // Enhanced error handling for backend validation errors
      if (error?.data && typeof error.data === 'object') {
        // Check if error response has the format: { status: false, errors: { field: ["message"] } }
        if (error.data.status === false && error.data.errors && typeof error.data.errors === 'object') {
          // Display each field error as separate toast
          Object.keys(error.data.errors).forEach(field => {
            const fieldErrors = error.data.errors[field];
            if (Array.isArray(fieldErrors)) {
              fieldErrors.forEach(errorMessage => {
                // Translate field names to Azerbaijani for better UX
                const fieldTranslations = {
                  'to_status_id': 'Yeni status',
                  'from_status_id': 'Əvvəlki status',
                  'reason': 'Səbəb',
                  'notes': 'Qeydlər',
                  'student_id': 'Tələbə',
                  'application_id': 'Müraciət'
                };
                
                const fieldName = fieldTranslations[field] || field;
                toast.error(`${fieldName}: ${errorMessage}`);
              });
            } else if (typeof fieldErrors === 'string') {
              const fieldTranslations = {
                'to_status_id': 'Yeni status',
                'from_status_id': 'Əvvəlki status',
                'reason': 'Səbəb',
                'notes': 'Qeydlər',
                'student_id': 'Tələbə',
                'application_id': 'Müraciət'
              };
              
              const fieldName = fieldTranslations[field] || field;
              toast.error(`${fieldName}: ${fieldErrors}`);
            }
          });
        }
        // Check if error response has a general message
        else if (error.data.message) {
          toast.error('Status dəyişdirilərkən xəta: ' + error.data.message);
        }
        // Fallback for other error formats
        else {
          toast.error('Status dəyişdirilərkən xəta baş verdi: ' + (error.message || 'Naməlum xəta'));
        }
      } else {
        // Fallback for non-object errors
        toast.error('Status dəyişdirilərkən xəta baş verdi: ' + (error.message || 'Naməlum xəta'));
      }
    }
  };

  const handleAcceptanceSubmit = async (e) => {
    e.preventDefault();
    try {
      const acceptancePayload = {
        student_id: selectedStudent.studentId || selectedStudent.id,
        application_id: selectedStudent.applicationId,
        offer_id: parseInt(acceptanceData.offerId),
        university_id: parseInt(acceptanceData.universityId),
        program_id: parseInt(acceptanceData.programId),
        acceptance_date: acceptanceData.acceptanceDate,
        start_date: acceptanceData.startDate || null,
        tuition_fee: acceptanceData.tuitionFee || null,
        scholarship_amount: acceptanceData.scholarshipAmount || null,
        notes: acceptanceData.notes || null,
        documents_required: acceptanceData.documentsRequired || null,
        accepted_by: 'İstifadəçi', // This should be the logged-in user
        accepted_by_id: 1 // This should be the logged-in user ID
      };
      
      await acceptUniversityOffer(acceptancePayload).unwrap();
      
      toast.success(`${selectedStudent.firstName} ${selectedStudent.lastName} tələbəsinin ${acceptanceData.universityName} universitetindən təklifi uğurla qəbul edildi!`);
      
      // Refresh data
      refetchApplications();
      if (acceptanceOffersResponse) {
        // Trigger refetch of offers for this student
        // The query will automatically refresh due to invalidatesTags
      }
      
      setIsAcceptanceModalOpen(false);
      setSelectedStudent(null);
      setAcceptanceData({
        offerId: '',
        universityId: '',
        universityName: '',
        programId: '',
        programName: '',
        acceptanceDate: new Date().toISOString().split('T')[0],
        startDate: '',
        tuitionFee: '',
        scholarshipAmount: '',
        notes: '',
        documentsRequired: ''
      });
    } catch (error) {
      console.error('Error accepting university offer:', error);
      
      // Enhanced error handling for backend validation errors
      if (error?.data && typeof error.data === 'object') {
        // Check if error response has the format: { status: false, errors: { field: ["message"] } }
        if (error.data.status === false && error.data.errors && typeof error.data.errors === 'object') {
          // Display each field error as separate toast
          Object.keys(error.data.errors).forEach(field => {
            const fieldErrors = error.data.errors[field];
            if (Array.isArray(fieldErrors)) {
              fieldErrors.forEach(errorMessage => {
                // Translate field names to Azerbaijani for better UX
                const fieldTranslations = {
                  'offer_id': 'Təklif',
                  'university_id': 'Universitet',
                  'program_id': 'Proqram',
                  'acceptance_date': 'Qəbul tarixi',
                  'start_date': 'Başlama tarixi',
                  'tuition_fee': 'Təhsil haqqı',
                  'scholarship_amount': 'Təqaüd məbləği',
                  'notes': 'Qeydlər',
                  'documents_required': 'Tələb olunan sənədlər',
                  'student_id': 'Tələbə',
                  'application_id': 'Müraciət'
                };
                
                const fieldName = fieldTranslations[field] || field;
                toast.error(`${fieldName}: ${errorMessage}`);
              });
            } else if (typeof fieldErrors === 'string') {
              const fieldTranslations = {
                'offer_id': 'Təklif',
                'university_id': 'Universitet',
                'program_id': 'Proqram',
                'acceptance_date': 'Qəbul tarixi',
                'start_date': 'Başlama tarixi',
                'tuition_fee': 'Təhsil haqqı',
                'scholarship_amount': 'Təqaüd məbləği',
                'notes': 'Qeydlər',
                'documents_required': 'Tələb olunan sənədlər',
                'student_id': 'Tələbə',
                'application_id': 'Müraciət'
              };
              
              const fieldName = fieldTranslations[field] || field;
              toast.error(`${fieldName}: ${fieldErrors}`);
            }
          });
        }
        // Check if error response has a general message
        else if (error.data.message) {
          toast.error('Təklifi qəbul edərkən xəta: ' + error.data.message);
        }
        // Fallback for other error formats
        else {
          toast.error('Təklifi qəbul edərkən xəta baş verdi: ' + (error.message || 'Naməlum xəta'));
        }
      } else {
        // Fallback for non-object errors
        toast.error('Təklifi qəbul edərkən xəta baş verdi: ' + (error.message || 'Naməlum xəta'));
      }
    }
  };

  const handleRecommendationSelect = (recommendationIndex, universityIndex) => {
    const key = `${recommendationIndex}-${universityIndex}`;
    if (selectedRecommendations.includes(key)) {
      setSelectedRecommendations(selectedRecommendations.filter(item => item !== key));
    } else {
      setSelectedRecommendations([...selectedRecommendations, key]);
    }
  };

  const handleAcceptSelectedRecommendations = () => {
    if (selectedRecommendations.length === 0) {
      toast.warning('Zəhmət olmasa, qəbul etmək istədiyiniz təklifləri seçin.');
      return;
    }

    const acceptedOffers = [];
    selectedRecommendations.forEach(key => {
      const [recIndex, uniIndex] = key.split('-').map(Number);
      const recommendation = viewingStudent.sentRecommendations[recIndex];
      const university = recommendation.universities[uniIndex];
      acceptedOffers.push({
        recommendationDate: recommendation.sentDate,
        universityName: university.universityName,
        programName: university.programName,
        degree: university.degree,
        tuition: university.tuition
      });
    });

    console.log('Accepted recommendations:', acceptedOffers);
    toast.success(`${acceptedOffers.length} təklif qəbul edildi: ${acceptedOffers.map(offer => `${offer.universityName} - ${offer.programName}`).join(', ')}`);
    setSelectedRecommendations([]);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setConsultantFilter('');
    setSourceFilter('');
    setEducationFilter('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Convert camelCase formData to snake_case for API
      const apiData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: getUnmaskedPhone(formData.phone), // Send unmasked phone number
        email: formData.email,
        birth_date: formData.birthDate || null,
        address: formData.address || null,
        parent_phone: formData.parentPhone || null,
        parent_name: formData.parentName || null,
        source: formData.source,
        first_contact_date: formData.firstContactDate,
        status_id: parseInt(formData.statusId),
        consultant_id: parseInt(formData.consultantId),
        education_level: formData.educationLevel,
        interested_education_level_id: formData.interestedEducationLevel ? parseInt(formData.interestedEducationLevel) : null,
        specialization: formData.specialization,
        current_year: formData.currentYear,
        current_university: formData.currentUniversity || null,
        university_id: formData.universityId ? parseInt(formData.universityId) : null,
        interested_countries: formData.interestedCountries || '',
        financial_forecast: formData.financialForecast,
        tuition_fee_range: formData.tuitionFeeRange,
        language_exam: formData.languageExam,
        language_exam_date: formData.languageExamDate || null,
        other_exams: formData.otherExams,
        gpa: formData.gpa,
        general_notes: formData.generalNotes,
        applied_universities: formData.appliedUniversities || null,
        required_documents: formData.requiredDocuments || null,
        follow_up_done: formData.followUpDone,
        last_follow_up_date: formData.lastFollowUpDate || null
      };
      
      if (editingStudent) {
        // Edit mode: Always include student_id since we're editing existing application
        const updateData = {
          ...apiData,
          student_id: editingStudent.studentId || existingStudent?.id || null
        };
        await updateApplication({ id: editingStudent.applicationId, obj: updateData }).unwrap();
        toast.success('Müraciət uğurla yeniləndi!');
      } else {
        // Add mode: Include student_id only if existing student found
        const addData = {
          ...apiData,
          student_id: existingStudent?.id || null // null if new student, existing ID if found
        };
        await addApplication(addData).unwrap();
        
        if (existingStudent) {
          toast.success('Mövcud tələbə üçün yeni müraciət əlavə edildi!');
        } else {
                      toast.success('Yeni tələbə və müraciət uğurla yaradıldı!');
        }
      }
      
      // Refresh applications list
      refetchApplications();
      
      setIsModalOpen(false);
      setEditingStudent(null);
      setExistingStudent(null);
      setFieldsDisabled(false);
      setPhoneQuery('');
      setCountryDropdownOpen(false);
    } catch (error) {
      console.error('Error saving application:', error);
      
      // Enhanced error handling for backend validation errors
      if (error?.data && typeof error.data === 'object') {
        // Check if error response has the format: { status: false, errors: { field: ["message"] } }
        if (error.data.status === false && error.data.errors && typeof error.data.errors === 'object') {
          // Display each field error as separate toast
          Object.keys(error.data.errors).forEach(field => {
            const fieldErrors = error.data.errors[field];
            if (Array.isArray(fieldErrors)) {
              fieldErrors.forEach(errorMessage => {
                // Translate field names to Azerbaijani for better UX
                const fieldTranslations = {
                  'email': 'Email',
                  'phone': 'Telefon',
                  'first_name': 'Ad',
                  'last_name': 'Soyad',
                  'source': 'Mənbə',
                  'consultant_id': 'Konsultant',
                  'specialization': 'İxtisas',
                  'education_level': 'Təhsil səviyyəsi',
                  'university_id': 'Universitet',
                  'status_id': 'Status'
                };
                
                const fieldName = fieldTranslations[field] || field;
                toast.error(`${fieldName}: ${errorMessage}`);
              });
            } else if (typeof fieldErrors === 'string') {
              const fieldTranslations = {
                'email': 'Email',
                'phone': 'Telefon',
                'first_name': 'Ad',
                'last_name': 'Soyad',
                'source': 'Mənbə',
                'consultant_id': 'Konsultant',
                'specialization': 'İxtisas',
                'education_level': 'Təhsil səviyyəsi',
                'university_id': 'Universitet',
                'status_id': 'Status'
              };
              
              const fieldName = fieldTranslations[field] || field;
              toast.error(`${fieldName}: ${fieldErrors}`);
            }
          });
        }
        // Check if error response has a general message
        else if (error.data.message) {
          toast.error('Xəta: ' + error.data.message);
        }
        // Fallback for other error formats
        else {
          toast.error('Xəta baş verdi: ' + (error.message || 'Naməlum xəta'));
        }
      } else {
        // Fallback for non-object errors
        toast.error('Xəta baş verdi: ' + (error.message || 'Naməlum xəta'));
      }
    }
  };

  // Check if university-program combination is already sent
  const isOfferAlreadySent = (universityId, programId) => {
    // Use selectedStudentOffersResponse for recommendations modal, universityOffersResponse for view modal
    const offersData = isUniversityModalOpen ? selectedStudentOffersResponse : universityOffersResponse;
    
    if (!offersData?.data?.recommendations) return false;
    
    return offersData.data.recommendations.some(offer => 
      offer.university_id === universityId && offer.program_id === programId
    );
  };

  const getStatusBadge = (status, statusId) => {
    const statusObj = statuses.find(s => s.id === statusId);
    if (!statusObj) return null;
    
    // Safely render the status name
    const statusName = status?.name 
      ? status.name 
      : typeof status === 'string' 
      ? status 
      : statusObj?.name || 'Naməlum status';
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        statusId === 6 ? 'bg-green-100 text-green-800 border border-green-200' :
        statusId === 4 ? 'bg-blue-100 text-blue-800 border border-blue-200' :
        statusId === 3 ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
        statusId === 2 ? 'bg-orange-100 text-orange-800 border border-orange-200' :
        'bg-gray-100 text-gray-800 border border-gray-200'
      }`}>
        {statusName}
      </span>
    );
  };

  // Loading state
  if (applicationsLoading || statusesLoading) {
    return (
      <div className='fixed inset-0 flex items-center bg-white opacity-[.5] justify-center z-50'>
        <Audio
          height="80"
          width="80"
          radius="9"
          color="green"
          ariaLabel="loading"
          wrapperStyle
          wrapperClass
        />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Tələbə Müraciətləri</h1>
          <p className="mt-1 text-sm text-gray-600">
            Bütün tələbə müraciətlərini idarə edin və universitet təklifləri göndərin
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200">
              Ümumi: {students.length}
            </span>
            {(searchTerm || statusFilter || consultantFilter || sourceFilter || educationFilter) && (
              <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-green-100 text-green-800 border border-green-200">
                Tapılan: {filteredStudents.length}
              </span>
            )}
          </div>
        </div>
        <div className="flex-shrink-0 flex gap-3">
          {canAddStudentRequest && (
            <button
              onClick={handleAdd}
              className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Yeni Müraciət
            </button>
          )}
          
          <button
            onClick={() => navigate('/request-statuses')}
            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            title="Müraciət statuslarını idarə edin"
          >
            <Cog6ToothIcon className="-ml-1 mr-2 h-5 w-5" />
            Status İdarəsi
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg p-4 sm:p-6 text-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <DocumentTextIcon className="h-6 w-6 sm:h-8 sm:w-8" />
            </div>
            <div className="ml-3 sm:ml-4 min-w-0">
              <p className="text-blue-100 text-xs sm:text-sm font-medium truncate">Ümumi Müraciətlər</p>
              <p className="text-xl sm:text-2xl font-bold">{students.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow-lg p-4 sm:p-6 text-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-6 w-6 sm:h-8 sm:w-8" />
            </div>
            <div className="ml-3 sm:ml-4 min-w-0">
              <p className="text-green-100 text-xs sm:text-sm font-medium truncate">Qəbul Almış</p>
              <p className="text-xl sm:text-2xl font-bold">{students.filter(s => s.statusId === 6).length}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow-lg p-4 sm:p-6 text-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClockIcon className="h-6 w-6 sm:h-8 sm:w-8" />
            </div>
            <div className="ml-3 sm:ml-4 min-w-0">
              <p className="text-orange-100 text-xs sm:text-sm font-medium truncate">Gözləyən</p>
              <p className="text-xl sm:text-2xl font-bold">{students.filter(s => [1, 2, 3, 4, 5].includes(s.statusId)).length}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow-lg p-4 sm:p-6 text-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-6 w-6 sm:h-8 sm:w-8" />
            </div>
            <div className="ml-3 sm:ml-4 min-w-0">
              <p className="text-purple-100 text-xs sm:text-sm font-medium truncate">Yeni (7 gün)</p>
              <p className="text-xl sm:text-2xl font-bold">{students.filter(s => {
                const contactDate = new Date(s.firstContactDate);
                const sevenDaysAgo = new Date();
                sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                return contactDate > sevenDaysAgo;
              }).length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Filters */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg border border-gray-200">
        <div className="flex items-center space-x-2 mb-4">
          <FunnelIcon className="h-5 w-5 text-gray-400" />
          <h3 className="text-base sm:text-lg font-medium text-gray-900">Filterlər</h3>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Ad, soyad, telefon, email..."
                className="block w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Bütün statuslar</option>
              {statuses.map(status => (
                <option key={status.id} value={status.id}>{status.name}</option>
              ))}
            </select>

            <select
              value={consultantFilter}
              onChange={(e) => setConsultantFilter(e.target.value)}
              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Bütün konsultantlar</option>
              {consultants.filter(c => c.isActive).map(consultant => (
                <option key={consultant.id} value={consultant.id}>{consultant.name}</option>
              ))}
            </select>

            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Bütün mənbələr</option>
              {sourcesOptions.map((source, index) => (
                <option key={index} value={source?.name || source}>
                  {source?.name || source}
                </option>
              ))}
            </select>

            <select
              value={educationFilter}
              onChange={(e) => setEducationFilter(e.target.value)}
              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Bütün təhsil səviyyələri</option>
              {educationLevels.map((level, index) => (
                <option key={index} value={level?.name || level}>
                  {level?.name || level}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={handleClearFilters}
            disabled={!searchTerm && !statusFilter && !consultantFilter && !sourceFilter && !educationFilter}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FunnelIcon className="-ml-1 mr-2 h-4 w-4" />
            Filtri təmizlə
          </button>
        </div>
      </div>



      {/* Students table */}
      <div className="bg-white shadow-lg overflow-hidden sm:rounded-xl border border-blue-100">
        {/* Table header with pagination controls */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div className="flex items-center space-x-4">
              <h3 className="text-lg font-semibold text-gray-900">Müraciətlər</h3>
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <span>
                  {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredStudents.length)} / {filteredStudents.length} nəticə
                </span>
                {filteredStudents.length !== totalApplications && (
                  <span className="text-blue-600">
                    (Ümumi: {totalApplications})
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-600">Səhifədə:</label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                  className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
              
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                
                <div className="flex items-center space-x-1">
                  {totalPages <= 7 ? (
                    // Show all pages if 7 or fewer
                    Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-1 text-sm rounded-md ${
                          currentPage === page
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {page}
                      </button>
                    ))
                  ) : (
                    // Show ellipsis for many pages
                    <>
                      {currentPage > 3 && (
                        <>
                          <button
                            onClick={() => handlePageChange(1)}
                            className="px-3 py-1 text-sm rounded-md text-gray-600 hover:bg-gray-100"
                          >
                            1
                          </button>
                          {currentPage > 4 && <span className="text-gray-400">...</span>}
                        </>
                      )}
                      
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                        if (page > totalPages) return null;
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-3 py-1 text-sm rounded-md ${
                              currentPage === page
                                ? 'bg-blue-600 text-white'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                      
                      {currentPage < totalPages - 2 && (
                        <>
                          {currentPage < totalPages - 3 && <span className="text-gray-400">...</span>}
                          <button
                            onClick={() => handlePageChange(totalPages)}
                            className="px-3 py-1 text-sm rounded-md text-gray-600 hover:bg-gray-100"
                          >
                            {totalPages}
                          </button>
                        </>
                      )}
                    </>
                  )}
                </div>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-blue-50 to-blue-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    <div className="flex items-center space-x-2">
                      <span>Tələbə</span>
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Əlaqə
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Təhsil
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Müraciət
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Status
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Əməliyyatlar
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {paginatedStudents.map((student) => (
                  <tr key={student.id} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 transition-all duration-200 group">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow mr-4">
                          <span className="text-sm font-bold text-white">
                            {student.firstName[0]}{student.lastName[0]}
                          </span>
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-gray-900 group-hover:text-blue-900 transition-colors">
                            {student.firstName} {student.lastName}
                          </div>
                          <div className="text-xs text-gray-500">
                            ID: {student.id} | {
                              student.source?.name 
                                ? student.source.name
                                : typeof student.source === 'string' 
                                ? student.source 
                                : 'Mənbə təyin edilməyib'
                            }
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center space-x-1 mb-1">
                          <PhoneIcon className="h-3 w-3 text-gray-400" />
                          <a href={`tel:${student.phone}`} className="text-blue-600 hover:underline">
                            {student.phone}
                          </a>
                        </div>
                        {student.email && (
                          <div className="flex items-center space-x-1">
                            <EnvelopeIcon className="h-3 w-3 text-gray-400" />
                            <a href={`mailto:${student.email}`} className="text-blue-600 hover:underline truncate">
                              {student.email}
                            </a>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="font-medium">{
                          student.educationLevel?.name 
                            ? student.educationLevel.name
                            : typeof student.educationLevel === 'string' 
                            ? student.educationLevel 
                            : 'Təhsil səviyyəsi təyin edilməyib'
                        }</div>
                        <div className="text-xs text-gray-500">{student.specialization}</div>
                        <div className="text-xs text-gray-500">GPA: {student.gpa}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center space-x-1 mb-1">
                          <CalendarDaysIcon className="h-3 w-3 text-gray-400" />
                          <span>{student.firstContactDate ? new Date(student.firstContactDate).toLocaleDateString('az-AZ') : 'Tarix yoxdur'}</span>
                        </div>
                        <div className="text-xs text-gray-500">{
                          student.consultant?.admin?.first_name 
                            ? `${student.consultant.admin.first_name} ${student.consultant.admin.last_name}`
                            : student.consultant?.name 
                            ? student.consultant.name
                            : typeof student.consultant === 'string' 
                            ? student.consultant 
                            : 'Konsultant təyin edilməyib'
                        }</div>
                        <div className="text-xs text-gray-500">{
                          Array.isArray(student.interestedCountries) 
                            ? student.interestedCountries.map(country => 
                                typeof country === 'object' ? country.name : country
                              ).join(', ')
                            : typeof student.interestedCountries === 'object' && student.interestedCountries?.name
                            ? student.interestedCountries.name
                            : typeof student.interestedCountries === 'string'
                            ? student.interestedCountries
                            : 'Seçilməyib'
                        }</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(student.status, student.statusId)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex justify-end space-x-2">
                        {canViewStudentRequest && (
                          <button
                            onClick={() => handleView(student)}
                            className="inline-flex items-center p-2 bg-green-100 text-green-600 hover:bg-green-200 hover:text-green-800 rounded-lg transition-all duration-200 hover:scale-105"
                            title="Ətraflı bax"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        )}
                        {canAcceptStudentRequest && (
                          <button
                            onClick={() => handleAcceptOffer(student)}
                            disabled={student.statusId === 6}
                            className={`inline-flex items-center p-2 rounded-lg transition-all duration-200 ${
                              student.statusId === 6 
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200 hover:text-emerald-800 hover:scale-105'
                            }`}
                            title={student.statusId === 6 ? "Qəbul edilmiş müraciətə təklif qəbul edilə bilməz" : "Təklifi qəbul et"}
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                        )}
                        {canChangeStudentRequestStatus && (
                          <button
                            onClick={() => handleChangeStatus(student)}
                            className="inline-flex items-center p-2 bg-orange-100 text-orange-600 hover:bg-orange-200 hover:text-orange-800 rounded-lg transition-all duration-200 hover:scale-105"
                            title="Status dəyiş"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                            </svg>
                          </button>
                        )}
                        {canManageUniversityRequests && (
                          <button
                            onClick={() => handleSendUniversityRecommendations(student)}
                            disabled={student.statusId === 6}
                            className={`inline-flex items-center p-2 rounded-lg transition-all duration-200 ${
                              student.statusId === 6 
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                : 'bg-purple-100 text-purple-600 hover:bg-purple-200 hover:text-purple-800 hover:scale-105'
                            }`}
                            title={student.statusId === 6 ? "Qəbul edilmiş müraciətə təklif göndərilə bilməz" : "Universitet təklifləri"}
                          >
                            <PaperAirplaneIcon className="h-4 w-4" />
                          </button>
                        )}
                        {canEditStudentRequest && (
                          <button
                            onClick={() => handleEdit(student)}
                            className="inline-flex items-center p-2 bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-800 rounded-lg transition-all duration-200 hover:scale-105"
                            title="Redaktə et"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        )}
                        {canDeleteStudentRequest && (
                          <button
                            onClick={() => handleDelete(student.applicationId, `${student.firstName} ${student.lastName}`)}
                            className="inline-flex items-center p-2 bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-800 rounded-lg transition-all duration-200 hover:scale-105"
                            title="Sil"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredStudents.length === 0 && (
            <div className="text-center py-16">
              <div className="mx-auto h-16 w-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mb-4">
                <UserIcon className="h-8 w-8 text-blue-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Heç bir müraciət tapılmadı</h3>
              <p className="text-sm text-gray-500 max-w-sm mx-auto">
                Axtarış şərtlərinizi dəyişin və ya yeni müraciət əlavə edin.
              </p>
              {canAddStudentRequest && (
                <button
                  onClick={handleAdd}
                  className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  İlk Müraciəti Əlavə Et
                </button>
              )}
            </div>
          )}
        </div>
      </div>


      {/* Continue with modals in next part */}
      
      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => { setIsModalOpen(false); setCountryDropdownOpen(false); }} />
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full sm:p-6">
              <div className="mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {editingStudent ? 'Müraciəti Redaktə Et' : 'Yeni Müraciət Əlavə Et'}
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      {editingStudent ? 'Müraciət məlumatlarını yeniləyin' : 'Yeni müraciət məlumatlarını daxil edin'}
                    </p>
                  </div>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <span className="text-2xl">&times;</span>
                  </button>
                </div>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Phone Number Section - Always First */}
                <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-6 border border-orange-200">
                  <h4 className="text-lg font-semibold text-orange-900 mb-4">
                    Telefon Nömrəsi 
                    {phoneQuery && phoneCheckResult && (
                      <span className="ml-2 text-sm font-normal">
                        {phoneCheckResult.status && phoneCheckResult.data ? '(Mövcud müştəri)' : '(Yeni müştəri)'}
                      </span>
                    )}
                  </h4>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Telefon Nömrəsi *
                      </label>
                      <input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => handlePhoneChange(e.target.value)}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                        placeholder="+994 50 234 56 78"
                        maxLength={17}
                      />
                      {phoneQuery && phoneCheckResult && phoneCheckResult.status && phoneCheckResult.data && (
                        <p className="mt-1 text-sm text-green-600">
                          ✓ Bu nömrə ilə müştəri tapıldı. Məlumatlar avtomatik dolduruldu.
                        </p>
                      )}
                      {phoneQuery && phoneCheckResult && (!phoneCheckResult.status || !phoneCheckResult.data) && (
                        <p className="mt-1 text-sm text-blue-600">
                          ℹ Bu nömrə ilə müştəri tapılmadı. Yeni müştəri üçün məlumatları daxil edin.
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Personal Information Section */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
                  <h4 className="text-lg font-semibold text-blue-900 mb-4">Şəxsi Məlumatlar</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Adı *
                      </label>
                      <input
                        type="text"
                        required
                        disabled={fieldsDisabled && !editingStudent}
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Soyadı *
                      </label>
                      <input
                        type="text"
                        required
                        disabled={fieldsDisabled && !editingStudent}
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        disabled={fieldsDisabled && !editingStudent}
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mənbə *
                      </label>
                      <select
                        required
                        value={formData.source}
                        onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Mənbə seçin</option>
                        {sourcesOptions.map((source, index) => (
                          <option key={index} value={source?.name || source}>
                            {source?.name || source}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Konsultant *
                      </label>
                      <select
                        required
                        value={formData.consultantId}
                        onChange={(e) => setFormData({ ...formData, consultantId: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Konsultant seçin</option>
                        {consultants.filter(c => c.isActive).map(consultant => (
                          <option key={consultant.id} value={consultant.id}>{consultant.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        İlk Müraciət Tarixi *
                      </label>
                      <input
                        type="date"
                        required
                        value={formData.firstContactDate}
                        onChange={(e) => setFormData({ ...formData, firstContactDate: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status *
                      </label>
                      <select
                        required
                        value={formData.statusId}
                        onChange={(e) => setFormData({ ...formData, statusId: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        {statuses.map(status => (
                          <option key={status.id} value={status.id}>{status.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Son Follow-up Tarixi
                      </label>
                      <input
                        type="date"
                        value={formData.lastFollowUpDate}
                        onChange={(e) => setFormData({ ...formData, lastFollowUpDate: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Academic Information Section */}
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-6 border border-green-200">
                  <h4 className="text-lg font-semibold text-green-900 mb-4">Təhsil Məlumatları</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Təhsil Səviyyəsi *
                      </label>
                      <select
                        required
                        value={formData.educationLevel}
                        onChange={(e) => setFormData({ ...formData, educationLevel: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                      >
                        <option value="">Səviyyə seçin</option>
                        {educationLevels.map((level, index) => (
                          <option key={index} value={level?.name || level}>
                            {level?.name || level}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Maraqli Olduğu Təhsil Səviyyəsi
                      </label>
                      <select
                        value={formData.interestedEducationLevel}
                        onChange={(e) => {
                          setFormData({ ...formData, interestedEducationLevel: e.target.value });
                        }}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                      >
                        <option value="">Səviyyə seçin</option>
                        {educationLevels.map((level, index) => {
                          return (
                            <option key={index} value={level?.id || level}>
                              {level?.name || level}
                            </option>
                          );
                        })}
                      </select>
                      <div className="text-xs text-gray-500 mt-1">
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        İxtisaslaşma *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.specialization}
                        onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Universitet *
                      </label>
                      <select
                        required
                        value={formData.universityId}
                        onChange={(e) => setFormData({ ...formData, universityId: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                      >
                        <option value="">Universiteti seçin</option>
                        {universities.filter(uni => uni.is_active !== false).map(university => (
                          <option key={university.id} value={university.id}>
                            {university.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cari İl/Kurs
                      </label>
                      <input
                        type="text"
                        value={formData.currentYear}
                        onChange={(e) => setFormData({ ...formData, currentYear: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        GPA
                      </label>
                      <input
                        type="text"
                        value={formData.gpa}
                        onChange={(e) => setFormData({ ...formData, gpa: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Dil İmtahanı
                      </label>
                      <input
                        type="text"
                        value={formData.languageExam}
                        onChange={(e) => setFormData({ ...formData, languageExam: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Dil İmtahan Tarixi
                      </label>
                      <input
                        type="date"
                        value={formData.languageExamDate}
                        onChange={(e) => setFormData({ ...formData, languageExamDate: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                      />
                    </div>

                    <div className="lg:col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Digər İmtahanlar
                      </label>
                      <input
                        type="text"
                        value={formData.otherExams}
                        onChange={(e) => setFormData({ ...formData, otherExams: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Application Information Section */}
                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-6 border border-purple-200">
                  <h4 className="text-lg font-semibold text-purple-900 mb-4">Müraciət Məlumatları</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Maraq Dairəsi Ölkələr *
                      </label>
                      <div className="relative country-dropdown">
                        {/* Custom Select2-style Multi-select */}
                        <div 
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus-within:ring-purple-500 focus-within:border-purple-500 bg-white min-h-[42px] cursor-pointer relative"
                          onClick={() => {
                            setCountryDropdownOpen(!countryDropdownOpen);
                            if (!countryDropdownOpen) {
                              setCountrySearchTerm('');
                            }
                          }}
                        >
                          <div className="flex flex-wrap gap-1">
                            {formData.interestedCountries ? (
                              formData.interestedCountries.split(',').filter(id => id.trim()).map(countryId => {
                                const country = countries.find(c => c.id.toString() === countryId.trim());
                                return country ? (
                                  <span key={countryId} className="inline-flex items-center px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded border border-purple-200">
                                    {country.name}
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const currentIds = formData.interestedCountries ? formData.interestedCountries.split(',').filter(id => id.trim()) : [];
                                        const updatedIds = currentIds.filter(id => id.trim() !== countryId.trim());
                                        setFormData({ ...formData, interestedCountries: updatedIds.join(',') });
                                      }}
                                      className="ml-1 inline-flex items-center justify-center h-3 w-3 rounded-full text-purple-600 hover:bg-purple-200 hover:text-purple-800"
                                    >
                                      <svg className="h-2 w-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                  </span>
                                ) : null;
                              })
                            ) : null}
                            {(!formData.interestedCountries || formData.interestedCountries === '') && (
                              <span className="text-gray-500 text-sm">Ölkələri seçin...</span>
                            )}
                          </div>
                          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                            <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>

                        {/* Dropdown */}
                        {countryDropdownOpen && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                            {/* Search input */}
                            <div className="p-2 border-b border-gray-200">
                              <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                                </div>
                                <input
                                  type="text"
                                  placeholder="Ölkə axtarın..."
                                  value={countrySearchTerm}
                                  onChange={(e) => setCountrySearchTerm(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="block w-full pl-10 pr-8 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                                />
                                {countrySearchTerm && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCountrySearchTerm('');
                                    }}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                                  >
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </div>
                            
                            {/* Countries list */}
                            <div className="max-h-48 overflow-auto">
                              {countries
                                .filter(c => c.isActive)
                                .filter(c => c.name.toLowerCase().includes(countrySearchTerm.toLowerCase()))
                                .map(country => {
                                  const isSelected = formData.interestedCountries && formData.interestedCountries.split(',').some(id => id.trim() === country.id.toString());
                                  return (
                                    <div
                                      key={country.id}
                                      className="flex items-center px-3 py-2 hover:bg-purple-50 cursor-pointer"
                                      onClick={() => {
                                        const currentIds = formData.interestedCountries ? formData.interestedCountries.split(',').filter(id => id.trim()) : [];
                                        let updatedIds;
                                        if (isSelected) {
                                          updatedIds = currentIds.filter(id => id.trim() !== country.id.toString());
                                        } else {
                                          updatedIds = [...currentIds, country.id.toString()];
                                        }
                                        setFormData({ ...formData, interestedCountries: updatedIds.join(',') });
                                        setCountrySearchTerm(''); // Clear search after selection
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => {}} // Handled by parent div click
                                        className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                      />
                                      <label className="ml-2 text-sm text-gray-900 cursor-pointer">
                                        {country.name}
                                      </label>
                                    </div>
                                  );
                                })}
                              
                              {/* No results message */}
                              {countries
                                .filter(c => c.isActive)
                                .filter(c => c.name.toLowerCase().includes(countrySearchTerm.toLowerCase()))
                                .length === 0 && (
                                <div className="px-3 py-2 text-sm text-gray-500 text-center">
                                  Heç bir ölkə tapılmadı
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        {formData.interestedCountries ? formData.interestedCountries.split(',').filter(id => id.trim()).length : 0} ölkə seçildi
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Maliyyə Proqnozu
                      </label>
                      <select
                        value={formData.financialForecast}
                        onChange={(e) => setFormData({ ...formData, financialForecast: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="">Maliyyə vəziyyətini seçin</option>
                        <option value="Valideynlər qarşılayacaq">Valideynlər qarşılayacaq</option>
                        <option value="Təqaüd axtarır">Təqaüd axtarır</option>
                        <option value="Ailə dəstəyi">Ailə dəstəyi</option>
                        <option value="Qarışıq maliyyələşdirmə">Qarışıq maliyyələşdirmə</option>
                        <option value="Təqaüd alıb">Təqaüd alıb</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Təhsil Haqqı Aralığı
                      </label>
                      <input
                        type="text"
                        value={formData.tuitionFeeRange}
                        onChange={(e) => setFormData({ ...formData, tuitionFeeRange: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>

                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={formData.followUpDone}
                        onChange={(e) => setFormData({ ...formData, followUpDone: e.target.checked })}
                        className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">
                        Follow-up edildi
                      </span>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ümumi Qeydlər
                      </label>
                      <textarea
                        rows={3}
                        value={formData.generalNotes}
                        onChange={(e) => setFormData({ ...formData, generalNotes: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 resize-none"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => { setIsModalOpen(false); setCountryDropdownOpen(false); setCountrySearchTerm(''); }}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Ləğv et
                  </button>
                  {(editingStudent ? canEditStudentRequest : canAddStudentRequest) && (
                    <button
                      type="submit"
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                    >
                      {editingStudent ? 'Yenilə' : 'Yadda saxla'}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced View Modal */}
      {isViewModalOpen && viewingStudent && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setIsViewModalOpen(false)} />
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-7xl sm:w-full sm:p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                    <span className="text-sm font-bold text-white">
                      {viewingStudent.firstName[0]}{viewingStudent.lastName[0]}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      {viewingStudent.firstName} {viewingStudent.lastName} - Ətraflı Məlumat
                    </h3>
                    {getStatusBadge(viewingStudent.status, viewingStudent.statusId)}
                  </div>
                </div>
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => setActiveTab('details')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'details'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Əsas Məlumatlar
                  </button>
                  <button
                    onClick={() => setActiveTab('academic')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'academic'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Təhsil Məlumatları
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab('recommendations');
                      setSelectedRecommendations([]);
                    }}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'recommendations'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Göndərilən Təkliflər ({universityOffersResponse?.data?.recommendations?.length || 0})
                  </button>
                  <button
                    onClick={() => setActiveTab('followup')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'followup'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Follow-up və Qeydlər
                  </button>
                  <button
                    onClick={() => setActiveTab('statusHistory')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'statusHistory'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Status Tarixçəsi ({statusHistoryResponse?.data?.status_history?.length || 0})
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              <div className="min-h-64">
                {activeTab === 'details' && (
                  <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="h-8 w-8 bg-blue-600 rounded-lg flex items-center justify-center">
                        <UserIcon className="h-5 w-5 text-white" />
                      </div>
                      <h4 className="text-lg font-semibold text-blue-900">Şəxsi və Əlaqə Məlumatları</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-white p-4 rounded-lg border border-blue-100">
                        <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Ad Soyad</div>
                        <div className="text-sm font-semibold text-gray-900">{viewingStudent.firstName} {viewingStudent.lastName}</div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-blue-100">
                        <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Telefon</div>
                        <div className="text-sm text-blue-600">
                          <a href={`tel:${viewingStudent.phone}`} className="hover:underline">
                            {viewingStudent.phone}
                          </a>
                        </div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-blue-100">
                        <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Email</div>
                        <div className="text-sm text-blue-600">
                          {viewingStudent.email ? (
                            <a href={`mailto:${viewingStudent.email}`} className="hover:underline">
                              {viewingStudent.email}
                            </a>
                          ) : 'Məlumat yoxdur'}
                        </div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-blue-100">
                        <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Mənbə</div>
                        <div className="text-sm text-gray-900">{
                          viewingStudent.source?.name 
                            ? viewingStudent.source.name
                            : typeof viewingStudent.source === 'string' 
                            ? viewingStudent.source 
                            : 'Mənbə təyin edilməyib'
                        }</div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-blue-100">
                        <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">İlk Müraciət</div>
                        <div className="text-sm text-gray-900">{viewingStudent.firstContactDate ? new Date(viewingStudent.firstContactDate).toLocaleDateString('az-AZ') : 'Tarix yoxdur'}</div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-blue-100">
                        <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Konsultant</div>
                        <div className="text-sm font-semibold text-gray-900">{
                          viewingStudent.consultant?.admin?.first_name 
                            ? `${viewingStudent.consultant.admin.first_name} ${viewingStudent.consultant.admin.last_name}`
                            : viewingStudent.consultant?.name 
                            ? viewingStudent.consultant.name
                            : typeof viewingStudent.consultant === 'string' 
                            ? viewingStudent.consultant 
                            : 'Konsultant təyin edilməyib'
                        }</div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-blue-100">
                        <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Maraq Dairəsi</div>
                        <div className="text-sm text-gray-900">{
                          (() => {
                            if (Array.isArray(viewingStudent.interestedCountries)) {
                              return viewingStudent.interestedCountries.map(countryIdOrName => {
                                // If it's an ID, find the country name
                                if (typeof countryIdOrName === 'string' || typeof countryIdOrName === 'number') {
                                  const country = countries.find(c => c.id.toString() === countryIdOrName.toString());
                                  if (country) {
                                    return country.name;
                                  }
                                }
                                // If it's already a name or object
                                return typeof countryIdOrName === 'object' ? countryIdOrName.name : countryIdOrName;
                              }).join(', ');
                            } else if (typeof viewingStudent.interestedCountries === 'object' && viewingStudent.interestedCountries?.name) {
                              return viewingStudent.interestedCountries.name;
                            } else if (typeof viewingStudent.interestedCountries === 'string') {
                              return viewingStudent.interestedCountries;
                            }
                            return 'Seçilməyib';
                          })()
                        }</div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-blue-100">
                        <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Maliyyə</div>
                        <div className="text-sm text-gray-900">{viewingStudent.financialForecast}</div>
                        <div className="text-xs text-gray-500 mt-1">{viewingStudent.tuitionFeeRange}</div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'academic' && (
                  <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-6 border border-green-200">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="h-8 w-8 bg-green-600 rounded-lg flex items-center justify-center">
                        <AcademicCapIcon className="h-5 w-5 text-white" />
                      </div>
                      <h4 className="text-lg font-semibold text-green-900">Təhsil və İmtahan Məlumatları</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-white p-4 rounded-lg border border-green-100">
                        <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Təhsil Səviyyəsi</div>
                        <div className="text-sm font-semibold text-gray-900">{
                          viewingStudent.educationLevel?.name 
                            ? viewingStudent.educationLevel.name
                            : typeof viewingStudent.educationLevel === 'string' 
                            ? viewingStudent.educationLevel 
                            : 'Təhsil səviyyəsi təyin edilməyib'
                        }</div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-green-100">
                        <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Maraqli Olduğu Təhsil Səviyyəsi</div>
                        <div className="text-sm font-semibold text-gray-900">{
                          viewingStudent.interestedEducationLevel
                            ? (educationLevels.find(level => level.id?.toString() === viewingStudent.interestedEducationLevel?.toString())?.name || 
                               educationLevels.find(level => level.name === viewingStudent.interestedEducationLevel)?.name ||
                               viewingStudent.interestedEducationLevel)
                            : 'Təyin edilməyib'
                        }</div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-green-100">
                        <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">İxtisas</div>
                        <div className="text-sm text-gray-900">{viewingStudent.specialization}</div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-green-100">
                        <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Cari İl</div>
                        <div className="text-sm text-gray-900">{viewingStudent.currentYear}</div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-green-100">
                        <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">GPA</div>
                        <div className="text-sm font-semibold text-gray-900">{viewingStudent.gpa}</div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-green-100">
                        <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Dil İmtahanı</div>
                        <div className="text-sm font-semibold text-gray-900">{viewingStudent.languageExam}</div>
                        {viewingStudent.languageExamDate && (
                          <div className="text-xs text-gray-500 mt-1">{new Date(viewingStudent.languageExamDate).toLocaleDateString('az-AZ')}</div>
                        )}
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-green-100 md:col-span-3">
                        <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Digər İmtahanlar</div>
                        <div className="text-sm text-gray-900">{viewingStudent.otherExams}</div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'recommendations' && (
                  <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-6 border border-purple-200">
                                      <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="h-8 w-8 bg-purple-600 rounded-lg flex items-center justify-center">
                        <PaperAirplaneIcon className="h-5 w-5 text-white" />
                      </div>
                      <h4 className="text-lg font-semibold text-purple-900">Göndərilən Universitet Təklifləri</h4>
                      {offersLoading && (
                        <span className="ml-2 text-sm text-blue-600">Yüklənir...</span>
                      )}
                      {universityOffersResponse && universityOffersResponse.data && universityOffersResponse.data.recommendations && (
                        <span className="ml-2 text-sm text-green-600">
                          ({universityOffersResponse.data.recommendations.length} təklif)
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="space-y-4">
                    {offersLoading ? (
                      <div className="text-center py-8">
                        <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm shadow rounded-md text-blue-500 bg-blue-100">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Təkliflər yüklənir...
                        </div>
                      </div>
                    ) : universityOffersResponse && universityOffersResponse.data && universityOffersResponse.data.recommendations && universityOffersResponse.data.recommendations.length > 0 ? (
                      universityOffersResponse.data.recommendations.map((offer, index) => (
                        <div key={offer.id || index} className="bg-white p-4 rounded-lg border border-purple-100">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              <div className="h-10 w-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center">
                                <span className="text-xs font-bold text-white">{index + 1}</span>
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">
                                  {offer.sent_date ? new Date(offer.sent_date).toLocaleDateString('az-AZ') : 'Tarix məlum deyil'}
                                </div>
                                <div className="text-sm text-gray-600">
                                  Göndərən: {offer.sent_by_user?.first_name && offer.sent_by_user?.last_name ? 
                                    `${offer.sent_by_user.first_name} ${offer.sent_by_user.last_name}` : 
                                    'Məlum deyil'}
                                </div>
                              </div>
                            </div>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              offer.student_response === 'accepted' ? 'bg-green-100 text-green-800 border border-green-200' :
                              offer.student_response === 'rejected' ? 'bg-red-100 text-red-800 border border-red-200' :
                              'bg-yellow-100 text-yellow-800 border border-yellow-200'
                            }`}>
                              {offer.student_response === 'accepted' ? 'Qəbul edildi' :
                               offer.student_response === 'rejected' ? 'Rədd edildi' : 'Gözləyir'}
                            </span>
                          </div>
                          
                          <div className="space-y-2">
                            {offer.notes && (
                              <div className="text-sm text-gray-600 mb-2">
                                <strong>Qeyd:</strong> {offer.notes}
                              </div>
                            )}
                            
                            <div className="bg-gray-50 p-3 rounded border border-gray-200">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900">{offer.university?.name || 'Universitet məlum deyil'}</div>
                                  <div className="text-sm text-gray-600">
                                    <div><strong>Ölkə:</strong> {offer.university?.country?.name || 'Məlum deyil'}</div>
                                    <div><strong>Şəhər:</strong> {offer.university?.city?.name || 'Məlum deyil'}</div>
                                    <div><strong>Proqram:</strong> {offer.program?.name || 'Proqram məlum deyil'}</div>
                                    {offer.program?.degree && (
                                      <div><strong>Dərəcə:</strong> {offer.program.degree}</div>
                                    )}
                                    {offer.program?.duration && (
                                      <div><strong>Müddət:</strong> {offer.program.duration}</div>
                                    )}
                                    {offer.program?.tuition_amount && (
                                      <div><strong>Təhsil haqqı:</strong> {offer.program.tuition_amount} {offer.program?.currency?.symbol || offer.program?.currency?.code || offer.program?.tuition_currency || ''}</div>
                                    )}
                                  </div>
                                </div>
                                {canAcceptStudentRequest && (
                                  <div className="flex flex-col items-end space-y-2 ml-3">
                                    <button
                                      onClick={() => {
                                        setAcceptanceData({
                                          universityId: offer.university?.id?.toString() || '',
                                          universityName: offer.university?.name || '',
                                          programId: offer.program?.id?.toString() || '',
                                          programName: offer.program?.name || '',
                                          acceptanceDate: new Date().toISOString().split('T')[0],
                                          startDate: '',
                                          tuitionFee: `${offer.program?.tuition_amount || ''} ${offer.program?.currency?.symbol || offer.program?.currency?.code || offer.program?.tuition_currency || ''}`,
                                          scholarshipAmount: '',
                                          notes: `${offer.sent_date ? new Date(offer.sent_date).toLocaleDateString('az-AZ') : ''} tarixində göndərilən təklifdən qəbul edildi`,
                                          documentsRequired: ''
                                        });
                                        setSelectedStudent(viewingStudent);
                                        setIsAcceptanceModalOpen(true);
                                      }}
                                      className="px-3 py-1 text-xs bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200 transition-colors font-medium"
                                      disabled={offer.student_response === 'accepted'}
                                    >
                                      {offer.student_response === 'accepted' ? '✓ Qəbul edilib' : '✓ Qəbul Et'}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <div className="mx-auto h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                          <PaperAirplaneIcon className="h-6 w-6 text-purple-400" />
                        </div>
                        <h5 className="text-lg font-medium text-gray-900 mb-2">Hələ təklif göndərilməyib</h5>
                        <p className="text-gray-500">Bu tələbəyə hələ universitet təklifləri göndərilməyib</p>
                      </div>
                    )}
                  </div>
                  </div>
                )}

                {activeTab === 'followup' && (
                  <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-6 border border-orange-200">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="h-8 w-8 bg-orange-600 rounded-lg flex items-center justify-center">
                        <CalendarDaysIcon className="h-5 w-5 text-white" />
                      </div>
                      <h4 className="text-lg font-semibold text-orange-900">Follow-up və Qeydlər</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white p-4 rounded-lg border border-orange-100">
                        <div className="text-xs font-medium text-orange-600 uppercase tracking-wide mb-1">Follow-up Status</div>
                        <div className="text-sm font-semibold text-gray-900">
                          {viewingStudent.followUpDone ? 'Edildi' : 'Edilməyib'}
                        </div>
                      </div>
                      {viewingStudent.lastFollowUpDate && (
                        <div className="bg-white p-4 rounded-lg border border-orange-100">
                          <div className="text-xs font-medium text-orange-600 uppercase tracking-wide mb-1">Son Follow-up</div>
                          <div className="text-sm text-gray-900">{new Date(viewingStudent.lastFollowUpDate).toLocaleDateString('az-AZ')}</div>
                        </div>
                      )}
                      <div className="bg-white p-4 rounded-lg border border-orange-100 md:col-span-2">
                        <div className="text-xs font-medium text-orange-600 uppercase tracking-wide mb-1">Ümumi Qeydlər</div>
                        <div className="text-sm text-gray-900">{viewingStudent.generalNotes || 'Qeyd yoxdur'}</div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'statusHistory' && (
                  <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-6 border border-orange-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="h-8 w-8 bg-orange-600 rounded-lg flex items-center justify-center">
                          <CalendarDaysIcon className="h-5 w-5 text-white" />
                        </div>
                        <h4 className="text-lg font-semibold text-orange-900">Status Dəyişiklik Tarixçəsi</h4>
                        {statusHistoryLoading && (
                          <span className="ml-2 text-sm text-blue-600">Yüklənir...</span>
                        )}
                        {statusHistoryResponse && statusHistoryResponse.data && statusHistoryResponse.data.status_history && (
                          <span className="ml-2 text-sm text-green-600">
                            ({statusHistoryResponse.data.status_history.length} dəyişiklik)
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="space-y-4">
                      {statusHistoryLoading ? (
                        <div className="text-center py-8">
                          <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm shadow rounded-md text-blue-500 bg-blue-100">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Status tarixçəsi yüklənir...
                          </div>
                        </div>
                      ) : statusHistoryResponse && statusHistoryResponse.data && statusHistoryResponse.data.status_history && statusHistoryResponse.data.status_history.length > 0 ? (
                        statusHistoryResponse.data.status_history.map((statusChange, index) => (
                          <div key={statusChange.id || index} className="bg-white p-4 rounded-lg border border-orange-100">
                            <div className="flex items-start space-x-4">
                              <div className="flex-shrink-0">
                                <div className="h-10 w-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center">
                                  <span className="text-xs font-bold text-white">{index + 1}</span>
                                </div>
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="text-sm font-medium text-gray-900">
                                    {statusChange.from_status ? (
                                      <span>
                                        "<span className="text-red-600">{statusChange.from_status.name}</span>" -dan 
                                        "<span className="text-green-600">{statusChange.to_status.name}</span>" -a dəyişdirildi
                                      </span>
                                    ) : (
                                      <span>
                                        İlk status: "<span className="text-green-600">{statusChange.to_status.name}</span>"
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {statusChange.change_date ? new Date(statusChange.change_date).toLocaleDateString('az-AZ') : ''} - {statusChange.change_time}
                                  </div>
                                </div>
                                
                                <div className="text-sm text-gray-600 mb-2">
                                  <strong>Dəyişdirən:</strong> {statusChange.changed_by_consultant?.admin?.first_name && statusChange.changed_by_consultant?.admin?.last_name ? 
                                    `${statusChange.changed_by_consultant.admin.first_name} ${statusChange.changed_by_consultant.admin.last_name}` : 
                                    statusChange.changed_by || 'Məlum deyil'}
                                </div>
                                
                                <div className="text-sm text-gray-600 mb-2">
                                  <strong>Səbəb:</strong> {statusChange.reason || 'Səbəb göstərilməyib'}
                                </div>
                                
                                {statusChange.notes && (
                                  <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                                    <strong>Qeyd:</strong> {statusChange.notes}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8">
                          <div className="mx-auto h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                            <CalendarDaysIcon className="h-6 w-6 text-orange-400" />
                          </div>
                          <h5 className="text-lg font-medium text-gray-900 mb-2">Status tarixçəsi yoxdur</h5>
                          <p className="text-gray-500">Bu tələbənin status dəyişiklik tarixçəsi tapılmadı</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                {canManageUniversityRequests && (
                  <button
                    onClick={() => handleSendUniversityRecommendations(viewingStudent)}
                    disabled={viewingStudent?.statusId === 6}
                    className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md ${
                      viewingStudent?.statusId === 6 
                        ? 'text-gray-400 bg-gray-300 cursor-not-allowed' 
                        : 'text-white bg-purple-600 hover:bg-purple-700'
                    }`}
                  >
                    <PaperAirplaneIcon className="-ml-1 mr-2 h-4 w-4" />
                    Universitet Təklifləri Göndər
                  </button>
                )}
                {canChangeStudentRequestStatus && (
                  <button
                    onClick={() => {
                      setIsViewModalOpen(false);
                      handleChangeStatus(viewingStudent);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700"
                  >
                    <svg className="-ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                    Status Dəyiş
                  </button>
                )}
                {canEditStudentRequest && (
                  <button
                    onClick={() => {
                      setIsViewModalOpen(false);
                      handleEdit(viewingStudent);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <PencilIcon className="-ml-1 mr-2 h-4 w-4" />
                    Redaktə Et
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* University Recommendations Modal */}
      {isUniversityModalOpen && selectedStudent && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => {
              setIsUniversityModalOpen(false);
              setRecommendationParams(null);
            }} />
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full sm:p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {selectedStudent.firstName} {selectedStudent.lastName} üçün Universitet Təklifləri
                  </h3>
                  <p className="text-sm text-gray-600">
                    Tələbəyə göndəriləcək universitetləri və proqramları seçin
                  </p>
                </div>
                <button
                  onClick={() => {
                    setIsUniversityModalOpen(false);
                    setRecommendationParams(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              <div className="space-y-6">
                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-6 border border-purple-200">
                  <h4 className="text-lg font-semibold text-purple-900 mb-4">Tələbə Məlumatları</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                    <div className="bg-white p-3 rounded border border-purple-100">
                      <div className="text-xs text-purple-600 font-medium mb-1">Təhsil Səviyyəsi</div>
                      <div className="text-sm font-semibold">{
                          selectedStudent.educationLevel?.name 
                            ? selectedStudent.educationLevel.name
                            : typeof selectedStudent.educationLevel === 'string' 
                            ? selectedStudent.educationLevel 
                            : 'Təhsil səviyyəsi təyin edilməyib'
                        }</div>
                    </div>
                    <div className="bg-white p-3 rounded border border-purple-100">
                      <div className="text-xs text-purple-600 font-medium mb-1">Maraq Dairəsi Təhsil Dərəcəsi</div>
                      <div className="text-sm font-semibold">{
                        selectedStudent.interestedEducationLevel
                          ? (educationLevels.find(level => level.id?.toString() === selectedStudent.interestedEducationLevel?.toString())?.name || 
                             educationLevels.find(level => level.name === selectedStudent.interestedEducationLevel)?.name ||
                             selectedStudent.interestedEducationLevel)
                          : 'Təyin edilməyib'
                      }</div>
                    </div>
                    <div className="bg-white p-3 rounded border border-purple-100">
                      <div className="text-xs text-purple-600 font-medium mb-1">İxtisas</div>
                      <div className="text-sm font-semibold">{selectedStudent.specialization}</div>
                    </div>
                    <div className="bg-white p-3 rounded border border-purple-100">
                      <div className="text-xs text-purple-600 font-medium mb-1">Maraq Dairəsi Ölkələr</div>
                      <div className="text-sm font-semibold">{
                          (() => {
                            if (Array.isArray(selectedStudent.interestedCountries)) {
                              return selectedStudent.interestedCountries.map(countryIdOrName => {
                                // If it's an ID, find the country name
                                if (typeof countryIdOrName === 'string' || typeof countryIdOrName === 'number') {
                                  const country = countries.find(c => c.id.toString() === countryIdOrName.toString());
                                  if (country) {
                                    return country.name;
                                  }
                                }
                                // If it's already a name or object
                                return typeof countryIdOrName === 'object' ? countryIdOrName.name : countryIdOrName;
                              }).join(', ');
                            } else if (typeof selectedStudent.interestedCountries === 'object' && selectedStudent.interestedCountries?.name) {
                              return selectedStudent.interestedCountries.name;
                            } else if (typeof selectedStudent.interestedCountries === 'string') {
                              // Handle comma-separated IDs from database
                              if (selectedStudent.interestedCountries.includes(',')) {
                                const countryIds = selectedStudent.interestedCountries.split(',').map(id => id.trim()).filter(id => id);
                                const countryNames = countryIds.map(countryId => {
                                  const country = countries.find(c => c.id.toString() === countryId);
                                  return country ? country.name : countryId;
                                }).filter(name => name);
                                return countryNames.length > 0 ? countryNames.join(', ') : 'Seçilməyib';
                              } else {
                                // Single ID or country name
                                const country = countries.find(c => c.id.toString() === selectedStudent.interestedCountries.toString());
                                return country ? country.name : selectedStudent.interestedCountries;
                              }
                            }
                            return 'Seçilməyib';
                          })()
                        }</div>
                    </div>
                    <div className="bg-white p-3 rounded border border-purple-100">
                      <div className="text-xs text-purple-600 font-medium mb-1">Maliyyə</div>
                      <div className="text-sm font-semibold">{selectedStudent.tuitionFeeRange}</div>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    Tövsiyyə olunan Universitetlər və Proqramlar
                    {recommendationsLoading && (
                      <span className="ml-2 text-sm text-blue-600">Yüklənir...</span>
                    )}
                    {selectedStudentOffersLoading && (
                      <span className="ml-2 text-sm text-orange-600">Göndərilmiş təkliflər yoxlanılır...</span>
                    )}
                    {recommendationsResponse && recommendationsResponse.status && (
                      <span className="ml-2 text-sm text-green-600">
                        ({recommendationsResponse.total_universities} universitet, {recommendationsResponse.total_programs} proqram tapıldı)
                      </span>
                    )}
                  </h4>
                  <div className="space-y-4">
                    {recommendationsLoading ? (
                      <div className="text-center py-8">
                        <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm shadow rounded-md text-blue-500 bg-blue-100">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Tövsiyyələr hazırlanır...
                        </div>
                      </div>
                    ) : recommendationsResponse && recommendationsResponse.status && recommendationsResponse.data && recommendationsResponse.data.length > 0 ? (
                      recommendationsResponse.data.map((university) => (
                      <div key={university.id} className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <div className="h-10 w-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center">
                                <span className="text-xs font-bold text-white">
                                  {university.name.split(' ').map(word => word[0]).join('').slice(0, 2)}
                                </span>
                              </div>
                              <div>
                                <h5 className="font-medium text-gray-900">{university.name}</h5>
                                <p className="text-sm text-gray-500">{university.city?.name || 'Şəhər məlum deyil'}, {university.country?.name || 'Ölkə məlum deyil'}</p>
                              </div>
                            </div>
                            
                            <div className="ml-13 space-y-2">
                              <div className="text-sm text-gray-600">
                                <strong>Ranking:</strong> #{university.ranking || 'N/A'} | 
                                <strong> Ortalama xərc:</strong> {university.average_cost || 'Məlum deyil'} | 
                                <strong> Son tarix:</strong> {university.submission_deadline || 'Məlum deyil'}
                              </div>
                              
                              <div className="text-sm text-gray-600">
                                <strong>Əlaqə:</strong> {university.focal_point || 'Məlum deyil'} ({university.email || 'Email yoxdur'})
                              </div>
                              
                              <div className="text-sm text-gray-600">
                                <strong>Təklif olunan proqramlar:</strong>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                                {university.programs && university.programs
                                  .filter(program => !isOfferAlreadySent(university.id, program.id))
                                  .map((program) => (
                                  <div key={program.id} className="flex items-center space-x-2 p-2 bg-gray-50 rounded border">
                                    <input
                                      type="checkbox"
                                      checked={selectedUniversities.some(s => s.universityId === university.id && s.programId === program.id)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedUniversities([...selectedUniversities, {
                                            universityId: university.id,
                                            universityName: university.name,
                                            programId: program.id,
                                            programName: program.name,
                                            degree: program.degree,
                                            duration: program.duration,
                                            tuition: `${program.tuition_amount} ${program.currency?.symbol || program.currency?.code || ''}`,
                                            deadline: university.submission_deadline
                                          }]);
                                        } else {
                                          setSelectedUniversities(selectedUniversities.filter(s => 
                                            !(s.universityId === university.id && s.programId === program.id)
                                          ));
                                        }
                                      }}
                                      className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                    />
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-medium text-gray-900">{program.name}</div>
                                      <div className="text-xs text-gray-500">
                                        {program.degree} - {program.duration} - {program.tuition_amount} {program.currency?.symbol || program.currency?.code || ''}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              
                              {/* Show info about already sent offers */}
                              {university.programs && university.programs.filter(program => isOfferAlreadySent(university.id, program.id)).length > 0 && (
                                <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-md">
                                  <div className="text-xs text-blue-700">
                                    <strong>Artıq göndərilmiş proqramlar:</strong> {university.programs.filter(program => isOfferAlreadySent(university.id, program.id)).map(p => p.name).join(', ')}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                                          ))
                    ) : (
                      <div className="text-center py-8">
                        <div className="text-gray-500">
                          Bu tələbəyə uyğun universitet təklifi tapılmadı.
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {selectedUniversities.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-green-900 mb-4">Seçilmiş Tövsiyələr ({selectedUniversities.length})</h4>
                    <div className="space-y-2">
                      {selectedUniversities.map((item, index) => (
                        <div key={index} className="flex items-center justify-between bg-white p-3 rounded border border-green-100">
                          <div>
                            <div className="font-medium text-gray-900">{item.universityName}</div>
                            <div className="text-sm text-gray-600">{item.programName} - {item.degree} - {item.tuition}</div>
                          </div>
                          <button
                            onClick={() => {
                              setSelectedUniversities(selectedUniversities.filter((_, i) => i !== index));
                            }}
                            className="text-red-600 hover:text-red-800"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setIsUniversityModalOpen(false);
                    setRecommendationParams(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Ləğv et
                </button>
                {canManageUniversityRequests && (
                  <button
                    onClick={async () => {
                      if (selectedUniversities.length > 0) {
                        try {
                          const offerData = {
                            student_id: selectedStudent.studentId || selectedStudent.id,
                            application_id: selectedStudent.applicationId || selectedStudent.application_id,
                            recommendations: selectedUniversities.map(item => ({
                              university_id: item.universityId,
                              program_id: item.programId,
                              recommended_by: 1, // You may want to get the current user ID
                              recommendation_date: new Date().toISOString().split('T')[0],
                              notes: `Təklif olunan universitet: ${item.universityName}, Proqram: ${item.programName}`
                            }))
                          };
                          
                          await sendUniversityOffers(offerData).unwrap();
                          
                          toast.success(`${selectedUniversities.length} universitet təklifi ${selectedStudent.firstName} ${selectedStudent.lastName} adlı tələbəyə göndərildi!`);
                          setIsUniversityModalOpen(false);
                          setSelectedUniversities([]);
                          setRecommendationParams(null);
                          
                          // Refresh applications and offers data
                          refetchApplications();
                          refetchSelectedStudentOffers();
                        } catch (error) {
                          console.error('Error sending university offers:', error);
                          
                          // Enhanced error handling for backend validation errors
                          if (error?.data && typeof error.data === 'object') {
                            // Check if error response has the format: { status: false, errors: { field: ["message"] } }
                            if (error.data.status === false && error.data.errors && typeof error.data.errors === 'object') {
                              // Display each field error as separate toast
                              Object.keys(error.data.errors).forEach(field => {
                                const fieldErrors = error.data.errors[field];
                                if (Array.isArray(fieldErrors)) {
                                  fieldErrors.forEach(errorMessage => {
                                    // Translate field names to Azerbaijani for better UX
                                    const fieldTranslations = {
                                      'student_id': 'Tələbə',
                                      'application_id': 'Müraciət',
                                      'university_id': 'Universitet',
                                      'program_id': 'Proqram',
                                      'recommendations': 'Təkliflər',
                                      'recommendation_date': 'Təklif tarixi',
                                      'notes': 'Qeydlər'
                                    };
                                    
                                    const fieldName = fieldTranslations[field] || field;
                                    toast.error(`${fieldName}: ${errorMessage}`);
                                  });
                                } else if (typeof fieldErrors === 'string') {
                                  const fieldTranslations = {
                                    'student_id': 'Tələbə',
                                    'application_id': 'Müraciət',
                                    'university_id': 'Universitet',
                                    'program_id': 'Proqram',
                                    'recommendations': 'Təkliflər',
                                    'recommendation_date': 'Təklif tarixi',
                                    'notes': 'Qeydlər'
                                  };
                                  
                                  const fieldName = fieldTranslations[field] || field;
                                  toast.error(`${fieldName}: ${fieldErrors}`);
                                }
                              });
                            }
                            // Check if error response has a general message
                            else if (error.data.message) {
                              toast.error('Universitet təklifləri göndərilərkən xəta: ' + error.data.message);
                            }
                            // Fallback for other error formats
                            else {
                              toast.error('Universitet təklifləri göndərilərkən xəta baş verdi: ' + (error.message || 'Naməlum xəta'));
                            }
                          } else {
                            // Fallback for non-object errors
                            toast.error('Universitet təklifləri göndərilərkən xəta baş verdi: ' + (error.message || 'Naməlum xəta'));
                          }
                        }
                      } else {
                        toast.warning('Zəhmət olmasa, ən azı bir universitet və proqram seçin.');
                      }
                    }}
                    disabled={selectedUniversities.length === 0}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Təklifləri Göndər ({selectedUniversities.length})
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Status Change Modal */}
      {isStatusModalOpen && selectedStudent && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setIsStatusModalOpen(false)} />
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center">
                    <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      Status Dəyiş
                    </h3>
                    <p className="text-sm text-gray-600">
                      {selectedStudent.firstName} {selectedStudent.lastName} üçün status dəyişin
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsStatusModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              <form onSubmit={handleStatusSubmit} className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg border">
                  <div className="text-sm font-medium text-gray-700 mb-2">Cari Status</div>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    selectedStudent.statusId === 6 ? 'bg-green-100 text-green-800 border border-green-200' :
                    selectedStudent.statusId === 4 ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                    selectedStudent.statusId === 3 ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
                    selectedStudent.statusId === 2 ? 'bg-orange-100 text-orange-800 border border-orange-200' :
                    'bg-gray-100 text-gray-800 border border-gray-200'
                  }`}>
{
                      selectedStudent.status?.name 
                        ? selectedStudent.status.name
                        : typeof selectedStudent.status === 'string' 
                        ? selectedStudent.status 
                        : 'Status təyin edilməyib'
                    }
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Yeni Status *
                  </label>
                  <select
                    required
                    value={statusChangeData.toStatusId}
                    onChange={(e) => setStatusChangeData({ ...statusChangeData, toStatusId: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  >
                    {statuses.map(status => (
                      <option key={status.id} value={status.id}>{status.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dəyişiklik Səbəbi *
                  </label>
                  <input
                    type="text"
                    required
                    value={statusChangeData.reason}
                    onChange={(e) => setStatusChangeData({ ...statusChangeData, reason: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Məsələn: Sənədlər təqdim edildi"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Əlavə Qeyd
                  </label>
                  <textarea
                    rows={3}
                    value={statusChangeData.notes}
                    onChange={(e) => setStatusChangeData({ ...statusChangeData, notes: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 resize-none"
                    placeholder="Əlavə məlumat və ya qeydlər..."
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setIsStatusModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Ləğv et
                  </button>
                  {canChangeStudentRequestStatus && (
                    <button
                      type="submit"
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700"
                    >
                      Status Dəyiş
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
      {/* University Offer Acceptance Modal */}
      {isAcceptanceModalOpen && selectedStudent && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setIsAcceptanceModalOpen(false)} />
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center">
                    <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      Universitet Təklifini Qəbul Et
                    </h3>
                    <p className="text-sm text-gray-600">
                      {selectedStudent.firstName} {selectedStudent.lastName} üçün qəbul məlumatlarını daxil edin
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsAcceptanceModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              <form onSubmit={handleAcceptanceSubmit} className="space-y-6">
                {/* University and Program Selection from Sent Offers */}
                <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-lg p-6 border border-emerald-200">
                  <h4 className="text-lg font-semibold text-emerald-900 mb-4">
                    Göndərilmiş Təkliflər Arasından Seçim
                    {acceptanceOffersLoading && (
                      <span className="ml-2 text-sm text-blue-600">Yüklənir...</span>
                    )}
                  </h4>
                  
                  {acceptanceOffersLoading ? (
                    <div className="text-center py-8">
                      <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm shadow rounded-md text-blue-500 bg-blue-100">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Təkliflər yüklənir...
                      </div>
                    </div>
                  ) : acceptanceOffersResponse && acceptanceOffersResponse.data && acceptanceOffersResponse.data.recommendations && acceptanceOffersResponse.data.recommendations.length > 0 ? (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Qəbul etmək istədiyiniz təklifi seçin *
                        </label>
                        <div className="space-y-2">
                          {acceptanceOffersResponse.data.recommendations
                            .filter(offer => offer.student_response !== 'accepted') // Only show non-accepted offers
                            .map((offer) => (
                            <div key={offer.id} className="flex items-center space-x-3 p-3 bg-white border border-emerald-200 rounded-md">
                              <input
                                type="radio"
                                name="selectedOffer"
                                value={offer.id}
                                checked={acceptanceData.offerId === offer.id.toString()}
                                onChange={() => {
                                  setAcceptanceData({
                                    ...acceptanceData,
                                    offerId: offer.id.toString(),
                                    universityId: offer.university_id.toString(),
                                    universityName: offer.university?.name || '',
                                    programId: offer.program_id.toString(),
                                    programName: offer.program?.name || '',
                                    tuitionFee: `${offer.program?.tuition_amount || ''} ${offer.program?.currency?.symbol || offer.program?.currency?.code || offer.program?.tuition_currency || ''}`,
                                  });
                                }}
                                className="h-4 w-4 text-emerald-600 border-gray-300 focus:ring-emerald-500"
                                required
                              />
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">
                                  {offer.university?.name || 'Universitet məlum deyil'}
                                </div>
                                <div className="text-sm text-gray-600">
                                  <div><strong>Proqram:</strong> {offer.program?.name || 'Proqram məlum deyil'}</div>
                                  <div><strong>Dərəcə:</strong> {offer.program?.degree || 'Məlum deyil'}</div>
                                  <div><strong>Müddət:</strong> {offer.program?.duration || 'Məlum deyil'}</div>
                                  {offer.program?.tuition_amount && (
                                    <div><strong>Təhsil haqqı:</strong> {offer.program.tuition_amount} {offer.program?.currency?.symbol || offer.program?.currency?.code || offer.program?.tuition_currency || ''}</div>
                                  )}
                                  <div><strong>Göndərilmə tarixi:</strong> {offer.sent_date ? new Date(offer.sent_date).toLocaleDateString('az-AZ') : 'Məlum deyil'}</div>
                                </div>
                                {offer.notes && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    <strong>Qeyd:</strong> {offer.notes}
                                  </div>
                                )}
                              </div>
                              <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                                offer.student_response === 'accepted' ? 'bg-green-100 text-green-800' :
                                offer.student_response === 'rejected' ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {offer.student_response === 'accepted' ? 'Qəbul edildi' :
                                 offer.student_response === 'rejected' ? 'Rədd edildi' : 'Gözləyir'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="mx-auto h-12 w-12 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                        <svg className="h-6 w-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h5 className="text-lg font-medium text-gray-900 mb-2">Qəbul ediləcək təklif yoxdur</h5>
                      <p className="text-gray-500">Bu tələbəyə hələ universitet təklifləri göndərilməyib və ya bütün təkliflər artıq qəbul edilib</p>
                    </div>
                  )}
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
                  <h4 className="text-lg font-semibold text-blue-900 mb-4">Qəbul Təfərrüatları</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Qəbul Tarixi *
                      </label>
                      <input
                        type="date"
                        required
                        value={acceptanceData.acceptanceDate}
                        onChange={(e) => setAcceptanceData({ ...acceptanceData, acceptanceDate: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Təhsilə Başlama Tarixi
                      </label>
                      <input
                        type="date"
                        value={acceptanceData.startDate}
                        onChange={(e) => setAcceptanceData({ ...acceptanceData, startDate: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Təhsil Haqqı
                      </label>
                      <input
                        type="text"
                        value={acceptanceData.tuitionFee}
                        onChange={(e) => setAcceptanceData({ ...acceptanceData, tuitionFee: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Məsələn: 8000 EUR"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Təqaüd Məbləği
                      </label>
                      <input
                        type="text"
                        value={acceptanceData.scholarshipAmount}
                        onChange={(e) => setAcceptanceData({ ...acceptanceData, scholarshipAmount: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Məsələn: 5000 EUR (varsa)"
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-6 border border-purple-200">
                  <h4 className="text-lg font-semibold text-purple-900 mb-4">Əlavə Məlumatlar</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tələb Olunan Sənədlər
                      </label>
                      <textarea
                        rows={3}
                        value={acceptanceData.documentsRequired}
                        onChange={(e) => setAcceptanceData({ ...acceptanceData, documentsRequired: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 resize-none"
                        placeholder="Məsələn: Pasport surəti, bank arayışı, sığorta..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Qeydlər
                      </label>
                      <textarea
                        rows={3}
                        value={acceptanceData.notes}
                        onChange={(e) => setAcceptanceData({ ...acceptanceData, notes: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 resize-none"
                        placeholder="Qəbul haqqında əlavə məlumatlar və qeydlər..."
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setIsAcceptanceModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Ləğv et
                  </button>
                  {canAcceptStudentRequest && (
                    <button
                      type="submit"
                      disabled={!acceptanceData.offerId || acceptanceOffersLoading || 
                        !acceptanceOffersResponse?.data?.recommendations?.some(offer => offer.student_response !== 'accepted')
                      }
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      ✓ Qəbulu Təsdiq Et
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 